import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Permission } from '../types/auth';
import UserManagement from './UserManagement';
import SessionManager from './SessionManager';
import { multiOpcuaService, TurbineServer, OPCUAData } from '../services/multiOpcuaService';
import './Dashboard.css';

interface DashboardProps {
  onLogout: () => void;
}

interface TurbineData {
  id: string;
  name: string;
  model: string;
  status: 'operational' | 'maintenance' | 'inactive' | 'warning';
  power: number;
  windSpeed: number;
  rpm: number;
  windDirection: number;
  yawAngle: number;
  voltage: number;
  current: number;
  frequency: number;
  powerFactor: number;
  generatorTemp: number;
  gearboxTemp: number;
  oilTemp: number;
  bearingTemp: number;
  vibration: number;
  hydraulicPressure: number;
  bladeAngle: number;
  torque: number;
}

interface ComplexData {
  [complexId: string]: {
    [parkId: string]: {
      [turbineId: string]: TurbineData;
    };
  };
}

const Dashboard: React.FC<DashboardProps> = ({ onLogout }) => {
  const { user, hasPermission } = useAuth();
  const [activeTab, setActiveTab] = useState('inicio');
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [configMenuOpen, setConfigMenuOpen] = useState(false);
  const [turbinasMenuOpen, setTurbinasMenuOpen] = useState(false);
  const [selectedTurbine, setSelectedTurbine] = useState<string | null>(null);
  const [realTimeData, setRealTimeData] = useState<ComplexData>({});
  const [lastUpdate, setLastUpdate] = useState<Date>(new Date());
  
  // Estado para sistema de alarme sonoro
  const [previousTurbineStates, setPreviousTurbineStates] = useState<{[key: string]: string}>({});
  const previousTurbineStatesRef = useRef<{[key: string]: string}>({});
  const [audioContext, setAudioContext] = useState<AudioContext | null>(null);
  
  // Estado para múltiplas notificações de falha
  const [failureNotifications, setFailureNotifications] = useState<Array<{
    id: string;
    turbine: TurbineData;
    type: 'failure' | 'alert';
    timestamp: Date;
    timer: number;
    acknowledged: boolean;
  }>>([]);
  
  // Estado para alarmes reconhecidos (para não emitir som)
  const [acknowledgedAlarms, setAcknowledgedAlarms] = useState<Set<string>>(new Set());

  // Estados para controle do servidor OPC UA
  const [opcuaServerStatus, setOpcuaServerStatus] = useState<'running' | 'stopped' | 'starting' | 'stopping'>('running');
  const [opcuaConnectionTested, setOpcuaConnectionTested] = useState(false);
  const [opcuaConfig, setOpcuaConfig] = useState({
    exported: false,
    lastExport: null as Date | null
  });

  // Estados removidos - usando controle manual da turbina principal
  // (código removido para limpeza)

  const [manualFaults, setManualFaults] = useState<Array<{
    id: string;
    name: string;
    description: string;
    severity: 'low' | 'medium' | 'high' | 'critical';
    active: boolean;
    timestamp: Date;
  }>>([]);

  const [availableFaultTypes] = useState([
    { id: 'temp_high', name: 'Temperatura Alta', description: 'Temperatura do gerador acima do limite' },
    { id: 'vibration_high', name: 'Vibração Excessiva', description: 'Vibração acima do limite operacional' },
    { id: 'oil_pressure_low', name: 'Pressão de Óleo Baixa', description: 'Pressão hidráulica insuficiente' },
    { id: 'grid_fault', name: 'Falha na Rede', description: 'Problema na conexão com a rede elétrica' },
    { id: 'blade_fault', name: 'Falha nas Pás', description: 'Problema no sistema de controle das pás' },
    { id: 'yaw_fault', name: 'Falha no Sistema Yaw', description: 'Problema no sistema de orientação' },
    { id: 'generator_fault', name: 'Falha do Gerador', description: 'Problema no gerador elétrico' },
    { id: 'gearbox_fault', name: 'Falha na Caixa de Engrenagem', description: 'Problema na caixa multiplicadora' }
  ]);

  // Estados para configuração de aerogerador
  const [turbineConfigForm, setTurbineConfigForm] = useState({
    id: '',
    name: '',
    model: 'WEG AGW110/2.1',
    url: 'opc.tcp://localhost',
    port: 4336,
    path: '/PrimeWindTurbine',
    requiresAuth: false,
    username: '',
    password: '',
    maxPower: 2.1,
    complex: '',
    park: ''
  });

  const [configStatus, setConfigStatus] = useState<{
    testing: boolean;
    connectionOk: boolean;
    authOk: boolean;
    adding: boolean;
    message: string;
  }>({
    testing: false,
    connectionOk: false,
    authOk: false,
    adding: false,
    message: ''
  });

  // Funções para configuração de aerogerador
  const handleConfigFormChange = (field: string, value: any) => {
    setTurbineConfigForm(prev => ({
      ...prev,
      [field]: value
    }));

    // Ajustar potência máxima baseada no modelo
    if (field === 'model') {
      let maxPower = 2.1;
      if (value.includes('147/4.2')) maxPower = 4.2;
      else if (value.includes('172/7.5')) maxPower = 7.5;
      
      setTurbineConfigForm(prev => ({
        ...prev,
        maxPower
      }));
    }

    // Limpar status quando mudanças são feitas
    setConfigStatus(prev => ({
      ...prev,
      connectionOk: false,
      authOk: false,
      message: ''
    }));
  };

  const testNewTurbineConnection = async () => {
    setConfigStatus(prev => ({ ...prev, testing: true, message: '🧪 Testando conexão OPC UA...' }));
    
    try {
      const testResult = await multiOpcuaService.testOpcuaConnection(turbineConfigForm);
      
      if (testResult.success) {
        setConfigStatus(prev => ({ 
          ...prev, 
          testing: false, 
          connectionOk: true,
          authOk: !testResult.requiresAuth || (!!turbineConfigForm.username && !!turbineConfigForm.password),
          message: `✅ ${testResult.message}${testResult.detectedVariables ? ` (${testResult.detectedVariables.length} variáveis detectadas)` : ''}`
        }));
        
        // Auto-configurar se autenticação foi detectada
        if (testResult.requiresAuth && !turbineConfigForm.requiresAuth) {
          setTurbineConfigForm(prev => ({
            ...prev,
            requiresAuth: true
          }));
        }
      } else {
        setConfigStatus(prev => ({ 
          ...prev, 
          testing: false, 
          connectionOk: false,
          authOk: false,
          message: `❌ ${testResult.message}`
        }));
        
        // Mostrar campos de autenticação se necessário
        if (testResult.requiresAuth && !turbineConfigForm.requiresAuth) {
          setTurbineConfigForm(prev => ({
            ...prev,
            requiresAuth: true
          }));
          setConfigStatus(prev => ({
            ...prev,
            message: '🔐 Servidor requer autenticação. Configure as credenciais e teste novamente.'
          }));
        }
      }
    } catch (error) {
      setConfigStatus(prev => ({ 
        ...prev, 
        testing: false, 
        connectionOk: false,
        authOk: false,
        message: '❌ Erro interno no teste de conexão'
      }));
    }
  };

  const addNewTurbine = async () => {
    if (!turbineConfigForm.id || !turbineConfigForm.name) {
      setConfigStatus(prev => ({ ...prev, message: '❌ ID e Nome são obrigatórios' }));
      return;
    }
    
    if (!turbineConfigForm.complex || !turbineConfigForm.park) {
      setConfigStatus(prev => ({ ...prev, message: '❌ Complexo e Parque Eólico são obrigatórios para integração na hierarquia' }));
      return;
    }

    setConfigStatus(prev => ({ ...prev, adding: true, message: '🔄 Adicionando aerogerador com auto-configuração...' }));
    
    try {
      const result = await multiOpcuaService.addTurbineConfiguration(turbineConfigForm);
      
      if (result.success) {
        setConfigStatus(prev => ({ 
          ...prev, 
          adding: false, 
          connectionOk: true,
          message: `✅ ${result.message}`
        }));
        
        // Limpar formulário
        setTurbineConfigForm({
          id: '',
          name: '',
          model: 'WEG AGW110/2.1',
          url: 'opc.tcp://localhost',
          port: 4336,
          path: '/PrimeWindTurbine',
          requiresAuth: false,
          username: '',
          password: '',
          maxPower: 2.1,
          complex: '',
          park: ''
        });
        
        // Forçar atualização da lista de turbinas
        setTimeout(() => window.location.reload(), 2000);
        
      } else {
        setConfigStatus(prev => ({ 
          ...prev, 
          adding: false, 
          connectionOk: false,
          message: `❌ ${result.message}`
        }));
      }
    } catch (error) {
      setConfigStatus(prev => ({ 
        ...prev, 
        adding: false, 
        connectionOk: false,
        message: '❌ Erro interno ao adicionar aerogerador'
      }));
    }
  };

  // Gerar curva de potência com pontos dispersos e stall embarrigado
  const generatePowerCurve = (model: string) => {
    let maxPower = 2.1;
    if (model.includes('147/4.2')) maxPower = 4.2;
    if (model.includes('172/7.5')) maxPower = 7.5;
    
    // Função para calcular curva de referência ideal (independente dos dados medidos)
    const getReferencePower = (windSpeed: number) => {
      if (windSpeed < 3) return 0;
      if (windSpeed >= 20) return 0;
      if (windSpeed > 11) return maxPower; // Potência nominal atingida exatamente em 11 m/s
      
      // Curva teórica fixa - não baseada em medições reais
      // Crescimento cúbico suave de 3 a 11 m/s
      const normalizedWind = (windSpeed - 3) / 8; // Normalizar 3-11 m/s para 0-1
      return maxPower * Math.pow(normalizedWind, 2.8); // Curva cúbica suave
    };
    
    // Função para calcular desvio de stall (embarrigado suave para baixo)
    const getStallDeviation = (windSpeed: number) => {
      if (windSpeed < 10 || windSpeed > 12) return 0;
      
      // Função senoidal para criar o "embarrigado" mais suave
      const stallCenter = 11; // Centro do stall
      const stallWidth = 1; // Largura do desvio
      const stallDepth = 0.15; // Profundidade reduzida para embarrigamento mais suave
      
      const distance = Math.abs(windSpeed - stallCenter);
      if (distance > stallWidth) return 0;
      
      // Desvio mais suave com curva gaussiana
      const normalizedDistance = distance / stallWidth;
      return -stallDepth * Math.exp(-2 * Math.pow(normalizedDistance, 2));
    };
    
    // Gerar pontos dispersos (scatter plot)
    const scatterPoints = [];
    const referencePoints = [];
    
    // Criar curva de referência
    for (let windSpeed = 0; windSpeed <= 20; windSpeed += 0.1) {
      const referencePower = getReferencePower(windSpeed);
      referencePoints.push({
        windSpeed: Number(windSpeed.toFixed(1)),
        power: Number(referencePower.toFixed(3)),
        isReference: true,
        efficiency: 0.98,
        isMeasured: false,
        isStall: false,
        isNominal: false
      });
    }
    
    // Gerar pontos de medição dispersos com ruído e stall
    for (let i = 0; i < 200; i++) {
      const windSpeed = 3 + Math.random() * 17; // 3-20 m/s
      const referencePower = getReferencePower(windSpeed);
      const stallDeviation = getStallDeviation(windSpeed);
      
      // Adicionar ruído realístico
      const baseNoise = (Math.random() - 0.5) * 0.15; // ±7.5% ruído base
      const windNoise = (Math.random() - 0.5) * 0.3; // Ruído adicional no vento
      
      // Calcular potência com stall e ruído
      let power = referencePower * (1 + stallDeviation) * (1 + baseNoise);
      power = Math.max(0, Math.min(maxPower, power));
      
      // Determinar cor baseada na velocidade do vento
      const isNominal = windSpeed >= 11 && windSpeed <= 20; // Zona nominal a partir de 11 m/s
      const isStallZone = windSpeed >= 10 && windSpeed <= 12;
      
      scatterPoints.push({
        windSpeed: Number((windSpeed + windNoise).toFixed(1)),
        power: Number(power.toFixed(3)),
        isReference: false,
        efficiency: isStallZone ? 0.85 : 0.98,
        isMeasured: true,
        isStall: isStallZone,
        isNominal: isNominal
      });
    }
    
    // Retornar curva de referência + pontos dispersos
    return [...referencePoints, ...scatterPoints];
  };

  // Gerar histórico de geração diária (últimos 7 dias) - dados fixos baseados no ID da turbina
  const generateDailyHistory = (turbineId: string) => {
    const history = [];
    const today = new Date();
    
    // Usar o ID da turbina como semente para gerar dados consistentes
    const seedHash = turbineId.split('').reduce((a, b) => { a = ((a << 5) - a) + b.charCodeAt(0); return a & a; }, 0);
    
    // Deterministic random function based on seed
    let seed = seedHash;
    const seededRandom = () => {
      const x = Math.sin(seed++) * 10000;
      return x - Math.floor(x);
    };
    
    const turbineData = getTurbineData(turbineId);
    const maxPower = turbineData?.model.includes('147/4.2') ? 4.2 : 
                    turbineData?.model.includes('172/7.5') ? 7.5 : 2.1;
    
    for (let i = 6; i >= 0; i--) {
      const date = new Date(today);
      date.setDate(date.getDate() - i);
      
      // Usar funções determinísticas baseadas no seed
      const baseGeneration = 0.3 + (seededRandom() * 0.6); // 30-90% da capacidade
      const weatherFactor = 0.8 + (seededRandom() * 0.3); // 80-110% (clima)
      const maintenanceFactor = seededRandom() > 0.92 ? 0.1 : 1; // 8% chance de manutenção
      
      // Geração em MWh (potência média * 24h)
      const dailyGeneration = maxPower * baseGeneration * weatherFactor * maintenanceFactor * 24;
      
      history.push({
        date: date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' }),
        fullDate: date.toLocaleDateString('pt-BR'),
        generation: Number(dailyGeneration.toFixed(1)),
        availability: maintenanceFactor > 0.5 ? Math.round(baseGeneration * 100).toString() : '10',
        windAverage: (8 + seededRandom() * 8).toFixed(1) // 8-16 m/s
      });
    }
    return history;
  };

  // Estado persistente do vento para simular condições meteorológicas reais
  const [windConditions, setWindConditions] = useState({
    baseSpeed: 12.5, // Velocidade base do vento
    direction: 225,   // Direção predominante
    lastUpdate: Date.now()
  });

  // Função para gerar alarme sonoro usando Web Audio API
  const playAlarmSound = async () => {
    try {
      // Inicializar AudioContext se necessário
      let ctx = audioContext;
      if (!ctx) {
        ctx = new (window.AudioContext || (window as any).webkitAudioContext)();
        setAudioContext(ctx);
      }
      
      // Reativar contexto se estiver suspenso (requisito do navegador)
      if (ctx.state === 'suspended') {
        await ctx.resume();
      }
      
      console.log('🚨 TOCANDO ALARME SONORO! 🚨');
      
      // Gerar som de alarme mais audível (triplo bipe)
      for (let i = 0; i < 3; i++) {
        const oscillator = ctx.createOscillator();
        const gainNode = ctx.createGain();
        
        oscillator.connect(gainNode);
        gainNode.connect(ctx.destination);
        
        // Frequência alternada para tornar mais perceptível
        const frequency = i % 2 === 0 ? 1000 : 600; // Alterna entre 1kHz e 600Hz
        oscillator.frequency.setValueAtTime(frequency, ctx.currentTime + i * 0.4);
        
        gainNode.gain.setValueAtTime(0, ctx.currentTime + i * 0.4);
        gainNode.gain.linearRampToValueAtTime(0.4, ctx.currentTime + i * 0.4 + 0.05);
        gainNode.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + i * 0.4 + 0.25);
        
        oscillator.start(ctx.currentTime + i * 0.4);
        oscillator.stop(ctx.currentTime + i * 0.4 + 0.25);
      }
    } catch (error) {
      console.error('❌ Erro no alarme sonoro:', error);
      alert('🚨 ALARME: Turbina entrou em falha! (Som não disponível)');
    }
  };

  // Função para detectar mudanças de status de turbinas
  const checkForAlarms = (newData: ComplexData) => {
    let changesDetected = 0;
    
    Object.keys(newData).forEach(complexId => {
      Object.keys(newData[complexId]).forEach(parkId => {
        Object.keys(newData[complexId][parkId]).forEach(turbineId => {
          const turbine = newData[complexId][parkId][turbineId];
          const previousStatus = previousTurbineStatesRef.current[turbineId];
          
          console.log(`🔍 DEBUG - Turbina ${turbineId}: Previous=${previousStatus}, Current=${turbine.status}`);
          
          // Detectar se turbina mudou de status (apenas mudanças reais)
          const isStatusChange = previousStatus && previousStatus !== turbine.status;
          
          if (isStatusChange) {
            changesDetected++;
            console.log(`📋 Mudança detectada: Turbina ${turbineId}: ${previousStatus || 'N/A'} → ${turbine.status}`);
            
            // Se turbina voltou a operacional, limpar alarmes reconhecidos para permitir novos alertas
            if (turbine.status === 'operational') {
              setAcknowledgedAlarms(prev => {
                const newSet = new Set(Array.from(prev));
                newSet.delete(turbineId);
                console.log(`🟢 Turbina ${turbineId} operacional - limpando reconhecimento de alarme`);
                return newSet;
              });
            }
            
            // Mostrar popup para todos os status críticos exceto operational
            if (turbine.status !== 'operational') {
              const statusMessages = {
                'inactive': '🚨 ALARME CRÍTICO: Turbina entrou em FALHA!',
                'warning': '⚠️ ALARME: Turbina entrou em ATENÇÃO!',
                'maintenance': '🔧 ALARME: Turbina entrou em MANUTENÇÃO!'
              };
              
              console.log(`${statusMessages[turbine.status]} Turbina ${turbineId}`);
              console.log(`🔔 Exibindo popup para ${turbine.status} da turbina ${turbineId}`);
              
              // Só tocar alarme e mostrar popup se não foi reconhecido
              if (!acknowledgedAlarms.has(turbineId)) {
                console.log(`🚨 TOCANDO ALARME SONORO para ${turbineId}! 🚨`);
                playAlarmSound();
                
                // Verificar se já existe uma notificação para esta turbina
                setFailureNotifications(prev => {
                  console.log(`📝 Adicionando notificação para turbina ${turbineId}`);
                  const existingIndex = prev.findIndex(notif => notif.turbine.id === turbineId);
                  if (existingIndex >= 0) {
                    // Atualizar notificação existente
                    const updated = [...prev];
                    updated[existingIndex] = {
                      ...updated[existingIndex],
                      turbine: { ...turbine, id: turbineId },
                      timestamp: new Date(),
                      timer: 10
                    };
                    console.log(`🔄 Atualizando notificação existente para ${turbineId}`);
                    return updated;
                  } else {
                    // Adicionar nova notificação
                    const newNotification = {
                      id: `${turbineId}-${Date.now()}`,
                      turbine: { ...turbine, id: turbineId },
                      type: turbine.status === 'inactive' ? 'failure' as const : 'alert' as const,
                      timestamp: new Date(),
                      timer: 10,
                      acknowledged: false
                    };
                    console.log(`✅ Nova notificação criada para ${turbineId}:`, newNotification);
                    return [...prev, newNotification];
                  }
                });
              } else {
                console.log(`🔇 Alarme reconhecido para ${turbineId} - popup e som desabilitados`);
              }

            }
          }
        });
      });
    });
    
    // Log apenas quando há mudanças para reduzir spam no console
    if (changesDetected > 0) {
      console.log(`📊 ${changesDetected} mudança(s) de status detectada(s)`);
    }
    
    // Atualizar estados anteriores usando ref para persistir entre renders
    Object.keys(newData).forEach(complexId => {
      Object.keys(newData[complexId]).forEach(parkId => {
        Object.keys(newData[complexId][parkId]).forEach(turbineId => {
          previousTurbineStatesRef.current[turbineId] = newData[complexId][parkId][turbineId].status;
        });
      });
    });
  };

  // Estado para dados OPC UA
  const [opcuaData, setOpcuaData] = useState<OPCUAData | null>(null);
  const [opcuaConnected, setOpcuaConnected] = useState(false);
  const [opcuaConnectionCount, setOpcuaConnectionCount] = useState(0);
  const [opcuaTotalTurbines, setOpcuaTotalTurbines] = useState(2);

  // Estado para controle manual override por turbina
  const [manualOverrides, setManualOverrides] = useState<{[key: string]: boolean}>({});
  const [manualControlData, setManualControlData] = useState<{[key: string]: Partial<TurbineData>}>({});
  
  // Estado para controle de disponibilidade do servidor HTTP (sempre true para sistema operacional)
  const [httpServerAvailable] = useState(true);

  // Função para enviar comando manual para o servidor OPC UA
  // Sistema de controle manual simplificado
  const [manualPower, setManualPower] = useState<{[key: string]: number}>({});
  const [manualWind, setManualWind] = useState<{[key: string]: number}>({});
  const [manualRpm, setManualRpm] = useState<{[key: string]: number}>({});
  const [manualTemp, setManualTemp] = useState<{[key: string]: number}>({});
  const [manualVibration, setManualVibrationState] = useState<{[key: string]: number}>({});
  
  // Função de controle manual
  const applyManualControl = (turbineId: string, parameter: string, value: number) => {
    console.log(`🎮 Controle manual aplicado [${turbineId}]: ${parameter} = ${value}`);
    
    switch(parameter) {
      case 'power':
        setManualPower(prev => ({...prev, [turbineId]: value}));
        break;
      case 'windSpeed': 
        setManualWind(prev => ({...prev, [turbineId]: value}));
        break;
      case 'rpm':
        setManualRpm(prev => ({...prev, [turbineId]: value}));
        break;
      case 'generatorTemp':
        setManualTemp(prev => ({...prev, [turbineId]: value}));
        break;
      case 'vibration':
        setManualVibrationState(prev => ({...prev, [turbineId]: value}));
        break;
    }
  };

  // Reset do controle manual
  const resetManualControl = (turbineId: string) => {
    console.log(`🔄 Reset controle manual [${turbineId}]`);
    setManualPower(prev => {const newState = {...prev}; delete newState[turbineId]; return newState;});
    setManualWind(prev => {const newState = {...prev}; delete newState[turbineId]; return newState;});
    setManualRpm(prev => {const newState = {...prev}; delete newState[turbineId]; return newState;});
    setManualTemp(prev => {const newState = {...prev}; delete newState[turbineId]; return newState;});
    setManualVibrationState(prev => {const newState = {...prev}; delete newState[turbineId]; return newState;});
  };

  // Função para gerar dados da turbina usando dados OPC UA ou controle manual
  const generateTurbineDataFromOPCUA = async (turbineId: string, model: string, previousData?: TurbineData): Promise<TurbineData> => {
    try {
      // O controle manual agora envia comandos diretamente para o servidor OPC UA
      // que por sua vez afeta os dados lidos aqui automaticamente
      
      // Obter dados do servidor OPC UA específico para a turbina
      const opcData = await multiOpcuaService.readTurbineData(turbineId);
      
      if (opcData) {
        setOpcuaData(opcData);
        setOpcuaConnected(true);
        
        // Converter dados OPC UA para formato da turbina
        const status = getStatusString(opcData.operationalStatus);
        
        // Verificar se há override manual para esta turbina
        const hasOverride = manualPower[turbineId] !== undefined || manualWind[turbineId] !== undefined || 
                           manualRpm[turbineId] !== undefined || manualTemp[turbineId] !== undefined || 
                           manualVibration[turbineId] !== undefined;
        
        // Determinar nome da turbina baseado no servidor e override
        const turbineName = turbineId === 'PW01-T-001' ? 
          `${turbineId} ${hasOverride ? '(Manual)' : '(OPC UA)'}` : 
          `${turbineId} (OPC UA Secure)`;
        
        // Calcular parâmetros derivados
        const windDirection = 225 + (Math.random() - 0.5) * 30;
        const yawAngle = windDirection + (Math.random() - 0.5) * 10;
        const voltage = 690 + (Math.random() - 0.5) * 4;
        
        // Aplicar overrides manuais ou usar dados OPC UA
        const finalPower = manualPower[turbineId] !== undefined ? manualPower[turbineId] : opcData.activePower;
        const finalWindSpeed = manualWind[turbineId] !== undefined ? manualWind[turbineId] : opcData.windSpeed;
        const finalRpm = manualRpm[turbineId] !== undefined ? manualRpm[turbineId] : opcData.rotorRPM;
        const finalTemp = manualTemp[turbineId] !== undefined ? manualTemp[turbineId] : opcData.generatorTemp;
        const finalVibration = manualVibration[turbineId] !== undefined ? manualVibration[turbineId] : opcData.vibration;
        
        const current = finalPower > 0 ? (finalPower * 1000) / voltage : 0;
        const frequency = 60 + (Math.random() - 0.5) * 0.1;
        const powerFactor = 0.98 + (Math.random() - 0.5) * 0.04;
        
        console.log(`🔗 Dados OPC UA recebidos - Potência: ${finalPower}MW, Vento: ${finalWindSpeed}m/s, Status: ${status}${hasOverride ? ' (Manual Override)' : ''}`);
        
        return {
          id: turbineId,
          name: turbineName,
          model: model,
          status: status,
          power: Number(finalPower.toFixed(2)),
          windSpeed: Number(finalWindSpeed.toFixed(1)),
          rpm: Number(finalRpm.toFixed(1)),
          windDirection: Math.floor(windDirection) % 360,
          yawAngle: Math.floor(yawAngle) % 360,
          voltage: Number(voltage.toFixed(1)),
          current: Number(current.toFixed(0)),
          frequency: Number(frequency.toFixed(2)),
          powerFactor: Number(powerFactor.toFixed(3)),
          generatorTemp: Number(finalTemp.toFixed(1)),
          gearboxTemp: Number((finalTemp - 3 + Math.random() * 2).toFixed(1)),
          oilTemp: Number((finalTemp - 15 + Math.random() * 5).toFixed(1)),
          bearingTemp: Number((finalTemp - 10 + Math.random() * 5).toFixed(1)),
          vibration: Number(finalVibration.toFixed(2)),
          hydraulicPressure: Number((175 + (Math.random() - 0.5) * 10).toFixed(0)),
          bladeAngle: Number(opcData.bladeAngle.toFixed(1)),
          torque: Number((finalPower * 1000000 / (finalRpm * 2 * Math.PI / 60) || 0).toFixed(0))
        };
      } else {
        throw new Error('Dados OPC UA não disponíveis');
      }
    } catch (error) {
      console.warn('⚠️ Falha na comunicação OPC UA, usando dados de fallback:', error);
      setOpcuaConnected(false);
      
      // Fallback para dados simulados em caso de falha OPC UA
      return generateFallbackData(turbineId, model, previousData);
    }
  };

  // Função para converter status numérico para string  
  const getStatusString = (status: number): 'operational' | 'warning' | 'maintenance' | 'inactive' => {
    switch (status) {
      case 1: return 'operational';
      case 2: return 'warning';
      case 3: return 'maintenance';
      case 0: return 'inactive';
      default: return 'operational';
    }
  };

  // Função de fallback para dados simulados quando OPC UA falha
  const generateFallbackData = (turbineId: string, model: string, previousData?: TurbineData): TurbineData => {
    const now = Date.now();
    
    let maxPower = 2.1;
    if (model.includes('147/4.2')) maxPower = 4.2;
    if (model.includes('172/7.5')) maxPower = 7.5;
    
    const turbineHash = turbineId.split('').reduce((a, b) => a + b.charCodeAt(0), 0);
    const timeBase = Math.floor(now / 5000);
    const windSpeed = 8 + Math.sin(timeBase * 0.1 + turbineHash) * 7 + Math.random() * 2;
    
    let powerOutput = 0;
    if (windSpeed >= 3 && windSpeed <= 25) {
      if (windSpeed <= 12) {
        powerOutput = Math.min(maxPower, (windSpeed - 3) * 0.23);
      } else {
        powerOutput = maxPower;
      }
    }
    powerOutput *= (0.95 + Math.random() * 0.1);
    
    const status: 'operational' | 'maintenance' | 'inactive' | 'warning' = 
      previousData?.status || (Math.random() < 0.95 ? 'operational' : 'warning');
    
    const rpm = windSpeed > 3 ? 8 + (windSpeed - 3) * 0.8 : 0;
    const generatorTemp = 45 + (powerOutput / maxPower) * 35 + Math.sin(timeBase * 0.05) * 5;
    
    return {
      id: turbineId,
      name: `${turbineId} (Simulado)`,
      model: model,
      status: status,
      power: Number(powerOutput.toFixed(2)),
      windSpeed: Number(windSpeed.toFixed(1)),
      rpm: Number(rpm.toFixed(1)),
      windDirection: 225,
      yawAngle: 225,
      voltage: 690,
      current: Number((powerOutput * 1000 / 690).toFixed(0)),
      frequency: 60,
      powerFactor: 0.98,
      generatorTemp: Number(generatorTemp.toFixed(1)),
      gearboxTemp: Number((generatorTemp - 5).toFixed(1)),
      oilTemp: Number((generatorTemp - 15).toFixed(1)),
      bearingTemp: Number((generatorTemp - 10).toFixed(1)),
      vibration: Number((1.5 + (rpm / 30) * 2).toFixed(2)),
      hydraulicPressure: 175,
      bladeAngle: Number((windSpeed > 15 ? 15 + (windSpeed - 15) * 2 : 0).toFixed(1)),
      torque: Number((powerOutput * 1000000 / (rpm * 2 * Math.PI / 60) || 0).toFixed(0))
    };
  };

  // Função para criar dados de turbina offline (sem configuração OPC UA)
  const createOfflineTurbine = (turbineId: string, model: string, previousData?: TurbineData): TurbineData => {
    return {
      id: turbineId,
      name: `${turbineId} (OFFLINE)`,
      model: model,
      status: 'inactive' as const,
      power: 0,
      windSpeed: 0,
      rpm: 0,
      windDirection: 0,
      yawAngle: 0,
      voltage: 0,
      current: 0,
      frequency: 0,
      powerFactor: 0,
      generatorTemp: 0,
      gearboxTemp: 0,
      oilTemp: 0,
      bearingTemp: 0,
      vibration: 0,
      hydraulicPressure: 0,
      bladeAngle: 0,
      torque: 0
    };
  };



  // Inicializar dados das turbinas dinamicamente baseado na configuração
  const initializeTurbinesData = (): ComplexData => {
    const complexData: ComplexData = {};
    
    // Obter turbinas configuradas
    const configuredTurbines = multiOpcuaService.getConfiguredTurbines();
    
    // Se não há turbinas configuradas, usar turbina padrão offline
    if (configuredTurbines.length === 0) {
      complexData['CE-PW-001'] = {
        'PE-PW-001': {
          'PW02-T-002': createOfflineTurbine('PW02-T-002', 'WEG AGW147/4.2', undefined)
        }
      };
      return complexData;
    }
    
    // Organizar turbinas por complexo e parque
    configuredTurbines.forEach(turbine => {
      if (!complexData[turbine.complex]) {
        complexData[turbine.complex] = {};
      }
      if (!complexData[turbine.complex][turbine.park]) {
        complexData[turbine.complex][turbine.park] = {};
      }
      
      // Inicializar com dados padrão
      complexData[turbine.complex][turbine.park][turbine.id] = generateFallbackData(turbine.id, turbine.model, undefined);
    });
    
    return complexData;
  };

  // Atualizar dados em tempo real usando turbinas configuradas dinamicamente
  const updateRealTimeData = async () => {
    try {
      const newData: ComplexData = {};
      let successfulConnections = 0;
      let totalConfiguredTurbines = 0;
      
      // Obter turbinas configuradas
      const configuredTurbines = multiOpcuaService.getConfiguredTurbines();
      console.log(`🔍 DASHBOARD DEBUG: ${configuredTurbines.length} turbinas configuradas:`, configuredTurbines.map(t => t.id));
      
      // Se não há turbinas configuradas, mostrar turbina offline padrão
      if (configuredTurbines.length === 0) {
        newData['CE-PW-001'] = {
          'PE-PW-001': {
            'PW02-T-002': createOfflineTurbine('PW02-T-002', 'WEG AGW147/4.2', undefined)
          }
        };
        setOpcuaConnectionCount(0);
        setOpcuaConnected(false);
        setRealTimeData(newData);
        setLastUpdate(new Date());
        return;
      }
      
      // Processar cada turbina configurada
      for (const turbineConfig of configuredTurbines) {
        totalConfiguredTurbines++;
        
        if (!newData[turbineConfig.complex]) {
          newData[turbineConfig.complex] = {};
        }
        if (!newData[turbineConfig.complex][turbineConfig.park]) {
          newData[turbineConfig.complex][turbineConfig.park] = {};
        }
        
        const currentTurbine = realTimeData[turbineConfig.complex]?.[turbineConfig.park]?.[turbineConfig.id];
        
        try {
          // Tentar obter dados OPC UA
          const updatedTurbine = await generateTurbineDataFromOPCUA(turbineConfig.id, turbineConfig.model, currentTurbine);
          newData[turbineConfig.complex][turbineConfig.park][turbineConfig.id] = updatedTurbine;
          
          if (updatedTurbine.name.includes('OPC UA')) {
            successfulConnections++;
          }
        } catch (error) {
          // Se falhar, mostrar como OFFLINE
          const offlineTurbine = createOfflineTurbine(turbineConfig.id, turbineConfig.model, currentTurbine);
          newData[turbineConfig.complex][turbineConfig.park][turbineConfig.id] = offlineTurbine;
        }
      }
      
      // Atualizar status de conexão OPC UA
      setOpcuaConnectionCount(successfulConnections);
      setOpcuaConnected(successfulConnections > 0);
      setOpcuaTotalTurbines(totalConfiguredTurbines);
      
      setRealTimeData(newData);
      setLastUpdate(new Date());
      
      console.log(`📊 DASHBOARD UPDATE: ${totalConfiguredTurbines} turbinas processadas, ${successfulConnections} conectadas`);
      console.log('🔍 DASHBOARD DATA STRUCTURE:', Object.keys(newData).map(complex => 
        Object.keys(newData[complex]).map(park => 
          `${complex}/${park}: ${Object.keys(newData[complex][park]).length} turbinas [${Object.keys(newData[complex][park]).join(', ')}]`
        )
      ).flat());
      
      // Verificar alarmes
      checkForAlarms(newData);
    } catch (error) {
      console.error('❌ Erro ao atualizar dados OPC UA:', error);
    }
  };

  // Função para obter dados de uma turbina específica
  const getTurbineData = (turbineId: string): TurbineData | null => {
    try {
      for (const complexId in realTimeData) {
        if (realTimeData[complexId]) {
          for (const parkId in realTimeData[complexId]) {
            if (realTimeData[complexId][parkId] && realTimeData[complexId][parkId][turbineId]) {
              return realTimeData[complexId][parkId][turbineId];
            }
          }
        }
      }
    } catch (error) {
      console.error('Erro ao buscar dados da turbina:', error);
    }
    return null;
  };

  // Calcular totais do sistema
  const calculateSystemTotals = () => {
    let totalPower = 0;
    let totalTurbines = 0;
    let operationalTurbines = 0;
    
    for (const complexId in realTimeData) {
      for (const parkId in realTimeData[complexId]) {
        for (const turbineId in realTimeData[complexId][parkId]) {
          const turbine = realTimeData[complexId][parkId][turbineId];
          totalPower += turbine.power;
          totalTurbines++;
          if (turbine.status === 'operational') operationalTurbines++;
        }
      }
    }
    
    return { totalPower, totalTurbines, operationalTurbines };
  };

  // Efeito para inicializar e atualizar dados
  useEffect(() => {
    // Inicializar dados
    const initialData = initializeTurbinesData();
    setRealTimeData(initialData);
    
    // Inicializar estados das turbinas para o sistema de alarme
    const initialStates: {[key: string]: string} = {};
    Object.keys(initialData).forEach(complexId => {
      Object.keys(initialData[complexId]).forEach(parkId => {
        Object.keys(initialData[complexId][parkId]).forEach(turbineId => {
          initialStates[turbineId] = initialData[complexId][parkId][turbineId].status;
        });
      });
    });
    setPreviousTurbineStates(initialStates);
    
    // Configurar interval para atualização a cada 5 segundos
    const interval = setInterval(() => {
      updateRealTimeData();
    }, 5000);
    
    return () => clearInterval(interval);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Funções para controle do servidor OPC UA
  const startOpcuaServer = async () => {
    setOpcuaServerStatus('starting');
    try {
      // Simular início do servidor
      await new Promise(resolve => setTimeout(resolve, 2000));
      setOpcuaServerStatus('running');
      console.log('🚀 Servidor OPC UA iniciado com sucesso!');
    } catch (error) {
      console.error('❌ Erro ao iniciar servidor OPC UA:', error);
      setOpcuaServerStatus('stopped');
    }
  };

  const stopOpcuaServer = async () => {
    setOpcuaServerStatus('stopping');
    try {
      // Simular parada do servidor
      await new Promise(resolve => setTimeout(resolve, 1500));
      setOpcuaServerStatus('stopped');
      console.log('🛑 Servidor OPC UA parado com sucesso!');
    } catch (error) {
      console.error('❌ Erro ao parar servidor OPC UA:', error);
      setOpcuaServerStatus('running');
    }
  };

  const testOpcuaConnection = async () => {
    setOpcuaConnectionTested(false);
    try {
      // Simular teste de conexão
      await new Promise(resolve => setTimeout(resolve, 3000));
      setOpcuaConnectionTested(true);
      console.log('✅ Teste de conexão OPC UA realizado com sucesso!');
      alert('✅ Conexão OPC UA testada com sucesso!\n\nServidor: opc.tcp://localhost:4334/PrimeWindTurbine\nStatus: Conectado\nVariáveis: 10 disponíveis\nLatência: 12ms');
    } catch (error) {
      console.error('❌ Erro no teste de conexão OPC UA:', error);
      alert('❌ Falha no teste de conexão OPC UA!\n\nVerifique se o servidor está em execução.');
    }
  };

  const exportOpcuaConfig = () => {
    const config = {
      servidor: {
        endereco: 'opc.tcp://localhost:4334/PrimeWindTurbine',
        ip: '127.0.0.1',
        porta: 4334,
        namespace: 'urn:PrimeWind:Turbine'
      },
      variaveis: [
        { nome: 'Potência Ativa', nodeId: 'ns=2;s=TurbineData.ActivePower', tipo: 'Double', unidade: 'MW' },
        { nome: 'Velocidade do Vento', nodeId: 'ns=2;s=TurbineData.WindSpeed', tipo: 'Double', unidade: 'm/s' },
        { nome: 'RPM do Rotor', nodeId: 'ns=2;s=TurbineData.RotorRPM', tipo: 'Double', unidade: 'rpm' },
        { nome: 'Temperatura do Gerador', nodeId: 'ns=2;s=TurbineData.GeneratorTemp', tipo: 'Double', unidade: '°C' },
        { nome: 'Temperatura da Caixa', nodeId: 'ns=2;s=TurbineData.GearboxTemp', tipo: 'Double', unidade: '°C' },
        { nome: 'Vibração', nodeId: 'ns=2;s=TurbineData.Vibration', tipo: 'Double', unidade: 'mm/s' },
        { nome: 'Status Operacional', nodeId: 'ns=2;s=TurbineData.OperationalStatus', tipo: 'String', unidade: '-' },
        { nome: 'Ângulo das Pás', nodeId: 'ns=2;s=TurbineData.BladeAngle', tipo: 'Double', unidade: '°' },
        { nome: 'Energia Acumulada', nodeId: 'ns=2;s=TurbineData.EnergyAccumulated', tipo: 'Double', unidade: 'MWh' },
        { nome: 'Disponibilidade', nodeId: 'ns=2;s=TurbineData.Availability', tipo: 'Double', unidade: '%' }
      ],
      configuracao: {
        frequenciaAtualizacao: '5 segundos',
        protocolo: 'OPC UA 1.04',
        seguranca: 'None (desenvolvimento)',
        exportadoEm: new Date().toISOString(),
        versao: '1.0.0'
      }
    };

    const jsonString = JSON.stringify(config, null, 2);
    const blob = new Blob([jsonString], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `prime-wind-opcua-config-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    setOpcuaConfig({
      exported: true,
      lastExport: new Date()
    });

    console.log('📄 Configuração OPC UA exportada com sucesso!');
  };

  // Efeito para auto-fechar popups após 10 segundos com contador regressivo
  useEffect(() => {
    if (failureNotifications.length > 0) {
      const countdownInterval = setInterval(() => {
        setFailureNotifications(prev => {
          return prev.map(notif => {
            if (notif.timer <= 1) {
              console.log(`⏰ Auto-fechando popup da turbina ${notif.turbine.id} após 10 segundos`);
              return null;
            }
            return { ...notif, timer: notif.timer - 1 };
          }).filter(notif => notif !== null) as Array<{
            id: string;
            turbine: TurbineData;
            type: 'failure' | 'alert';
            timestamp: Date;
            timer: number;
            acknowledged: boolean;
          }>;
        });
      }, 1000);

      return () => clearInterval(countdownInterval);
    }
  }, [failureNotifications]);

  // Efeito para verificar alarmes quando dados são atualizados
  useEffect(() => {
    if (Object.keys(realTimeData).length > 0) {
      checkForAlarms(realTimeData);
    }
  }, [realTimeData]);

  // Auto-limpeza de alarmes para simular resolução de problemas
  useEffect(() => {
    const cleanupInterval = setInterval(() => {
      // A cada 3 minutos, limpar 50-80% dos alarmes aleatoriamente
      const cleanupChance = Math.random();
      if (cleanupChance < 0.7) { // 70% de chance de limpeza
        setFailureNotifications(prev => {
          const keepCount = Math.floor(prev.length * (0.2 + Math.random() * 0.3)); // Manter 20-50%
          const shuffled = [...prev].sort(() => Math.random() - 0.5);
          const toKeep = shuffled.slice(0, keepCount);
          console.log(`🧹 Auto-limpeza: mantendo ${toKeep.length} de ${prev.length} alarmes`);
          return toKeep;
        });
        
        // Também limpar alguns alarmes reconhecidos
        setAcknowledgedAlarms(prev => {
          const newSet = new Set<string>();
          const alarmsArray = Array.from(prev);
          const keepCount = Math.floor(alarmsArray.length * 0.3); // Manter apenas 30%
          const shuffled = alarmsArray.sort(() => Math.random() - 0.5);
          shuffled.slice(0, keepCount).forEach(id => newSet.add(id));
          return newSet;
        });
      }
    }, 180000); // 3 minutos

    return () => clearInterval(cleanupInterval);
  }, []);

  // Handler global para promises rejeitadas não tratadas
  useEffect(() => {
    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      console.warn('⚠️ Promise rejection interceptada:', {
        reason: event.reason,
        type: typeof event.reason,
        message: event.reason?.message || 'No message',
        stack: event.reason?.stack || 'No stack trace'
      });
      event.preventDefault(); // Previne o erro no console
    };
    
    window.addEventListener('unhandledrejection', handleUnhandledRejection);
    
    return () => {
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    };
  }, []);

  const systemTotals = calculateSystemTotals();

  // Função para gerar histórico de alarmes baseado nas turbinas configuradas
  const generateHistoricalAlarms = () => {
    const turbineIds = Object.values(realTimeData).flatMap(complex => 
      Object.values(complex).flatMap(park => 
        Object.keys(park)
      )
    );

    const alarmTypes = [
      { type: 'warning', description: 'Temperatura do gerador elevada', frequency: 0.3 },
      { type: 'maintenance', description: 'Manutenção preventiva programada', frequency: 0.2 },
      { type: 'warning', description: 'Vibração acima do normal', frequency: 0.25 },
      { type: 'warning', description: 'Velocidade do vento muito baixa', frequency: 0.15 },
      { type: 'maintenance', description: 'Calibração de sensores', frequency: 0.1 }
    ];

    const alarms: any[] = [];
    const now = new Date();

    // Gerar alarmes dos últimos 7 dias
    for (let day = 0; day < 7; day++) {
      const alarmDate = new Date(now.getTime() - (day * 24 * 60 * 60 * 1000));
      
      // Cada turbina pode ter 0-3 alarmes por dia
      turbineIds.forEach(turbineId => {
        const numAlarms = Math.floor(Math.random() * 4); // 0-3 alarmes
        
        for (let i = 0; i < numAlarms; i++) {
          const alarmType = alarmTypes[Math.floor(Math.random() * alarmTypes.length)];
          const alarmTime = new Date(alarmDate);
          alarmTime.setHours(Math.floor(Math.random() * 24), Math.floor(Math.random() * 60));
          
          alarms.push({
            id: `${turbineId}-${alarmTime.getTime()}`,
            turbineId,
            type: alarmType.type,
            description: alarmType.description,
            timestamp: alarmTime,
            resolved: day > 1 ? Math.random() > 0.3 : Math.random() > 0.7 // Alarmes mais antigos têm mais chance de estar resolvidos
          });
        }
      });
    }

    return alarms.sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());
  };

  // Função para calcular estatísticas de alarmes
  const calculateAlarmStatistics = () => {
    const historicalAlarms = generateHistoricalAlarms();
    const last7Days = historicalAlarms.filter(alarm => 
      alarm.timestamp.getTime() > (Date.now() - 7 * 24 * 60 * 60 * 1000)
    );

    // Encontrar turbina com mais alarmes
    const turbineAlarmCount: { [key: string]: number } = {};
    last7Days.forEach(alarm => {
      turbineAlarmCount[alarm.turbineId] = (turbineAlarmCount[alarm.turbineId] || 0) + 1;
    });

    const mostAlarmsTurbine = Object.keys(turbineAlarmCount).reduce((a, b) => 
      turbineAlarmCount[a] > turbineAlarmCount[b] ? a : b, 
      Object.keys(turbineAlarmCount)[0] || 'N/A'
    );

    // Encontrar tipo mais frequente
    const typeCount: { [key: string]: number } = {};
    last7Days.forEach(alarm => {
      typeCount[alarm.type] = (typeCount[alarm.type] || 0) + 1;
    });

    const mostFrequentType = Object.keys(typeCount).reduce((a, b) => 
      typeCount[a] > typeCount[b] ? a : b, 
      Object.keys(typeCount)[0] || 'N/A'
    );

    const typeLabels: { [key: string]: string } = {
      'warning': 'Atenção',
      'maintenance': 'Manutenção',
      'inactive': 'Falha'
    };

    return {
      totalLast7Days: last7Days.length,
      mostAlarmsTurbine: mostAlarmsTurbine !== 'N/A' ? mostAlarmsTurbine : 'Nenhuma',
      mostAlarmsCount: turbineAlarmCount[mostAlarmsTurbine] || 0,
      mostFrequentType: typeLabels[mostFrequentType] || 'Nenhum',
      mostFrequentCount: typeCount[mostFrequentType] || 0
    };
  };

  const alarmStats = calculateAlarmStatistics();

  // Função para simular alarmes baseados em condições operacionais (muito raro)
  const simulateOperationalAlarms = () => {
    const currentAlarms: any[] = [];
    const now = new Date();
    
    // Apenas gerar alarmes com 1% de chance global
    if (Math.random() > 0.01) {
      return currentAlarms; // 99% das vezes não gera nenhum alarme
    }

    Object.values(realTimeData).forEach(complex => 
      Object.values(complex).forEach(park => 
        Object.values(park).forEach(turbine => {
          // Simular condições de alarme baseadas em parâmetros reais
          const alarmConditions = [];

          // Apenas gerar alarmes em condições extremas e com baixa frequência
          const alarmProbability = Math.random();
          
          // Temperatura do gerador extremamente crítica (acima de 100°C) e apenas com 5% de chance
          if (turbine.generatorTemp > 100 && alarmProbability < 0.05) {
            alarmConditions.push({
              type: 'warning',
              description: `Temperatura crítica do gerador: ${turbine.generatorTemp.toFixed(1)}°C`,
              parameter: 'temperature'
            });
          }

          // Vibração extremamente alta (acima de 6.0) e apenas com 3% de chance
          if (turbine.vibration > 6.0 && alarmProbability < 0.03) {
            alarmConditions.push({
              type: 'warning',
              description: `Vibração extrema: ${turbine.vibration.toFixed(2)} mm/s`,
              parameter: 'vibration'
            });
          }

          // Condições de vento/potência extremamente anormais e apenas com 2% de chance
          if (turbine.windSpeed < 2 && turbine.power > 1.5 && alarmProbability < 0.02) {
            alarmConditions.push({
              type: 'warning',
              description: `Anomalia severa: vento ${turbine.windSpeed.toFixed(1)}m/s, potência ${turbine.power.toFixed(1)}MW`,
              parameter: 'sensor'
            });
          }

          // Falha severa de geração apenas com 1% de chance
          if (turbine.windSpeed > 20 && turbine.power < 0.2 && alarmProbability < 0.01) {
            alarmConditions.push({
              type: 'warning',
              description: `Falha severa: vento ${turbine.windSpeed.toFixed(1)}m/s, potência ${turbine.power.toFixed(1)}MW`,
              parameter: 'performance'
            });
          }

          // Criar alarmes para as condições detectadas
          alarmConditions.forEach((condition, index) => {
            currentAlarms.push({
              id: `${turbine.id}-${condition.parameter}-${now.getTime()}-${index}`,
              turbineId: turbine.id,
              turbine: turbine,
              type: condition.type === 'warning' ? 'alert' : 'failure',
              description: condition.description,
              timestamp: now,
              acknowledged: false
            });
          });
        })
      )
    );

    return currentAlarms;
  };

  // Simular alguns alarmes operacionais baseados nas condições atuais
  const operationalAlarms = simulateOperationalAlarms();

  return (
    <div className="dashboard-container">
      {/* Menu Superior */}
      <header className="dashboard-header">
        <div className="header-left">
          <img src="/logo.png" alt="Prime Wind" className="header-logo" />
          <div className="title-container">
            <h1 className="header-title">SCADA</h1>
            <span className="version-text">DEMO VERSION</span>
          </div>
        </div>
        
        <nav className="header-nav">
          <span className="system-status">Sistema Online</span>

          <span 
            className="alarm-status clickable" 
            onClick={() => setActiveTab('alarmes')}
            title="Clique para ver detalhes dos alarmes"
          >
            <div className="alarm-indicator"></div>
            Alarmes: {failureNotifications.length + operationalAlarms.length}
          </span>
          <span className="update-indicator">
            <div className="update-pulse"></div>
            Atualização: {lastUpdate.toLocaleTimeString()}
          </span>
          <span className={`http-status ${httpServerAvailable ? 'available' : 'unavailable'}`}>
            <div className={`status-dot ${httpServerAvailable ? 'online' : 'offline'}`}></div>
            HTTP: {httpServerAvailable ? 'Ativo' : 'Inativo'}
          </span>
        </nav>

        <div className="header-right">
          <span className="user-info">{user?.fullName || 'Usuário'}</span>
          <button className="logout-button" onClick={onLogout}>
            Sair
          </button>
        </div>
      </header>

      <div className="dashboard-body">
        {/* Menu Lateral */}
        <aside className={`sidebar ${sidebarOpen ? 'open' : 'closed'}`}>
          <div className="sidebar-header">
            <h3>Configurações</h3>
            <button 
              className="sidebar-toggle"
              onClick={() => setSidebarOpen(!sidebarOpen)}
              title={sidebarOpen ? 'Fechar menu' : 'Abrir menu'}
            >
              {sidebarOpen ? '◀' : '▶'}
            </button>
          </div>
          
          <nav className="sidebar-nav">
            <button 
              className={`sidebar-item ${activeTab === 'inicio' ? 'active' : ''}`}
              onClick={() => setActiveTab('inicio')}
            >
              {sidebarOpen && <span>Início</span>}
            </button>
            
            <button 
              className={`sidebar-item ${activeTab === 'turbinas' || activeTab === 'opcua-simulator' || activeTab === 'turbine-simulator' ? 'active' : ''}`}
              onClick={() => {
                if (turbinasMenuOpen) {
                  setTurbinasMenuOpen(false);
                } else {
                  setTurbinasMenuOpen(true);
                  setActiveTab('turbinas');
                }
              }}
            >
              {sidebarOpen && (
                <>
                  <span>Turbinas</span>
                  <span className={`submenu-arrow ${turbinasMenuOpen ? 'open' : ''}`}>▼</span>
                </>
              )}
            </button>
            
            {sidebarOpen && turbinasMenuOpen && (
              <div className="submenu">
                <button 
                  className={`submenu-item ${activeTab === 'opcua-simulator' ? 'active' : ''}`}
                  onClick={() => setActiveTab('opcua-simulator')}
                >
                  OPC UA Simulator
                </button>
                <button 
                  className={`submenu-item ${activeTab === 'turbine-simulator' ? 'active' : ''}`}
                  onClick={() => setActiveTab('turbine-simulator')}
                >
                  Turbine Simulator
                </button>
                <button 
                  className={`submenu-item ${activeTab === 'turbine-config' ? 'active' : ''}`}
                  onClick={() => setActiveTab('turbine-config')}
                >
                  Configurar Aerogerador
                </button>
              </div>
            )}
            
            <button 
              className={`sidebar-item ${activeTab === 'monitoramento' ? 'active' : ''}`}
              onClick={() => setActiveTab('monitoramento')}
            >
              {sidebarOpen && <span>Monitoramento</span>}
            </button>
            
            <button 
              className={`sidebar-item ${activeTab === 'alarmes' ? 'active' : ''}`}
              onClick={() => setActiveTab('alarmes')}
            >
              {sidebarOpen && <span>Alarmes</span>}
            </button>
            
            <button 
              className={`sidebar-item ${activeTab === 'analise' ? 'active' : ''}`}
              onClick={() => setActiveTab('analise')}
            >
              {sidebarOpen && <span>Análise</span>}
            </button>
            
            <button 
              className={`sidebar-item ${activeTab === 'relatorios' ? 'active' : ''}`}
              onClick={() => setActiveTab('relatorios')}
            >
              {sidebarOpen && <span>Relatórios</span>}
            </button>

            <div className="sidebar-divider"></div>

            {hasPermission(Permission.USER_MANAGEMENT) && (
              <button 
                className={`sidebar-item ${activeTab === 'usuarios' ? 'active' : ''}`}
                onClick={() => setActiveTab('usuarios')}
              >
                {sidebarOpen && <span>Usuários</span>}
              </button>
            )}
            
            {hasPermission(Permission.SYSTEM_ADMIN) && (
              <button 
                className={`sidebar-item ${activeTab === 'sessoes' ? 'active' : ''}`}
                onClick={() => setActiveTab('sessoes')}
              >
                {sidebarOpen && <span>Sessões</span>}
              </button>
            )}
            
            <button 
              className={`sidebar-item ${activeTab.startsWith('config') ? 'active' : ''}`}
              onClick={() => {
                setConfigMenuOpen(!configMenuOpen);
                if (!configMenuOpen) setActiveTab('configuracoes');
              }}
            >
              {sidebarOpen && (
                <>
                  <span>Configurações</span>
                  <span className={`submenu-arrow ${configMenuOpen ? 'open' : ''}`}>▼</span>
                </>
              )}
            </button>
            
            {sidebarOpen && configMenuOpen && (
              <div className="submenu">
                <button 
                  className={`submenu-item ${activeTab === 'config-projeto' ? 'active' : ''}`}
                  onClick={() => setActiveTab('config-projeto')}
                >
                  Cadastro de Projeto
                </button>
                <button 
                  className={`submenu-item ${activeTab === 'config-aerogerador' ? 'active' : ''}`}
                  onClick={() => setActiveTab('config-aerogerador')}
                >
                  Cadastro de Aerogerador
                </button>
              </div>
            )}
          </nav>
        </aside>

        {/* Conteúdo Principal */}
        <main className="dashboard-main">
        {activeTab === 'inicio' && (
          <div className="dashboard-content">
            <div className="content-header">
              <h2>Monitoramento de Projetos Prime Wind</h2>
              <div className="status-indicators">
                <div className="status-item">
                  <span className="status-led active"></span>
                  <span>Sistema Operacional</span>
                </div>
                <div className="status-item">
                  <span className="status-led warning"></span>
                  <span>3 Alertas</span>
                </div>
                <div className="status-item">
                  <span className="status-led critical"></span>
                  <span>1 Crítico</span>
                </div>
              </div>
            </div>

            {/* Visão Geral dos Projetos */}
            <div className="projects-overview">
              <div className="project-summary-cards">
                <div className="summary-card">
                  <h3>Total de Complexos</h3>
                  <div className="metric-display">
                    <span className="metric-value">1</span>
                    <span className="metric-unit">Complexo</span>
                  </div>
                </div>
                
                <div className="summary-card">
                  <h3>Conexão OPC UA</h3>
                  <div className="metric-display">
                    <span className={`metric-value ${opcuaConnected ? 'connected' : 'disconnected'}`}>
                      {opcuaConnected ? 'ATIVO' : 'INATIVO'}
                    </span>
                    <span className="metric-unit">{opcuaConnected ? `${opcuaConnectionCount}/${opcuaTotalTurbines} Conectadas` : 'Status'}</span>
                  </div>
                </div>
                
                <div className="summary-card">
                  <h3>Turbinas Monitoradas</h3>
                  <div className="metric-display">
                    <span className="metric-value">{opcuaTotalTurbines}</span>
                    <span className="metric-unit">Aerogeradores</span>
                  </div>
                  <div className="metric-info">
                    <small>Distribuídas em 13 parques eólicos</small>
                  </div>
                </div>
                
                <div className="summary-card">
                  <h3>Potência Atual</h3>
                  <div className="metric-display">
                    <span className="metric-value">{systemTotals.totalPower.toFixed(1)}</span>
                    <span className="metric-unit">MW</span>
                  </div>
                  <div className="metric-info">
                    <small>Última atualização: {lastUpdate.toLocaleTimeString()}</small>
                  </div>
                </div>
              </div>
            </div>

            {/* Informações dos Complexos */}
            <div className="complexes-dashboard">
              <h3>Complexo Eólico Prime Wind</h3>
              
              {/* Complexo Prime Wind */}
              <div className="complex-card">
                <div className="complex-header">
                  <h4>Complexo Eólico Prime Wind</h4>
                  <span className="complex-code">CE-PW-001</span>
                </div>
                
                <div className="complex-info">
                  <div className="info-row">
                    <span>Localização:</span>
                    <span>Bahia, BA</span>
                  </div>
                  <div className="info-row">
                    <span>Potência Total:</span>
                    <span>300.3 MW</span>
                  </div>
                  <div className="info-row">
                    <span>Turbinas:</span>
                    <span>{opcuaTotalTurbines} turbinas • Multi-servidor OPC UA</span>
                  </div>
                </div>

                <div className="parks-container">
                  {/* Mostrar parques de todos os complexos */}
                  {Object.keys(realTimeData).map(complexId => (
                    realTimeData[complexId] && Object.keys(realTimeData[complexId]).map((parkId) => {
                      const parkData = realTimeData[complexId][parkId];
                      const parkNumber = parkId.split('-')[2];
                      
                      return (
                        <div key={parkId} className="park-card">
                          <div className="park-header">
                            <h5>Parque PW-{parkNumber}</h5>
                            <span className="park-code">{parkId}</span>
                          </div>
                          
                          <div className="park-summary">
                            <span>{Object.keys(parkData).length} aerogeradores</span>
                          </div>

                          <div className="turbines-grid">
                            {Object.values(parkData).map(turbine => (
                              <div key={turbine.id} className="turbine-item clickable" onClick={() => { 
                                setSelectedTurbine(turbine.id); 
                                setActiveTab('turbine-details'); 
                              }}>
                                <div className="turbine-id">{(turbine.id.split('-').pop() || '').padStart(3, '0')}</div>
                                <div className="turbine-status">
                                  <svg className="turbine-icon" viewBox="0 0 24 24">
                                    <line x1="12" y1="8" x2="12" y2="20" className={`turbine-tower ${turbine.status}`} strokeWidth="2"/>
                                    <g className={`turbine-rotor ${turbine.status}`}>
                                      <line x1="12" y1="8" x2="12" y2="3" className={`turbine-blades ${turbine.status}`} strokeWidth="1.5"/>
                                      <line x1="12" y1="8" x2="7.7" y2="12.3" className={`turbine-blades ${turbine.status}`} strokeWidth="1.5"/>
                                      <line x1="12" y1="8" x2="16.3" y2="12.3" className={`turbine-blades ${turbine.status}`} strokeWidth="1.5"/>
                                      <circle cx="12" cy="8" r="0.8" className={`turbine-blades ${turbine.status}`}/>
                                    </g>
                                  </svg>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    })
                  )).flat()}
                </div>
              </div>
            </div>

            {/* Condições Ambientais */}
            <div className="environmental-conditions">
              <h3>Condições Ambientais Atuais</h3>
              <div className="conditions-grid">
                <div className="condition-card">
                  <h4>Velocidade do Vento</h4>
                  <div className="metric-display">
                    <span className="metric-value">12.8</span>
                    <span className="metric-unit">m/s</span>
                  </div>
                </div>

                <div className="condition-card">
                  <h4>Direção do Vento</h4>
                  <div className="metric-display">
                    <span className="metric-value">245°</span>
                    <span className="metric-unit">SW</span>
                  </div>
                </div>

                <div className="condition-card">
                  <h4>Temperatura</h4>
                  <div className="metric-display">
                    <span className="metric-value">22.5</span>
                    <span className="metric-unit">°C</span>
                  </div>
                </div>

                <div className="condition-card">
                  <h4>Eficiência Geral</h4>
                  <div className="metric-display">
                    <span className="metric-value">85.3</span>
                    <span className="metric-unit">%</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Alertas Recentes */}
            <div className="alerts-section">
              <h3>Alertas Recentes</h3>
              <div className="alerts-list">
                <div className="alert-item critical">
                  <span className="alert-time">14:30</span>
                  <span className="alert-message">SJA-T-002 - Aerogerador em manutenção programada</span>
                  <span className="alert-park">Complexo São João</span>
                </div>
                <div className="alert-item warning">
                  <span className="alert-time">13:45</span>
                  <span className="alert-message">VSN-T-001 - Vibração elevada detectada</span>
                  <span className="alert-park">Complexo Ventos do Sul</span>
                </div>
                <div className="alert-item info">
                  <span className="alert-time">12:20</span>
                  <span className="alert-message">Sistema - Condições de vento ideais</span>
                  <span className="alert-park">Todos os parques</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Tela de Monitoramento Operacional */}
        {activeTab === 'monitoramento' && (
          <div className="operational-dashboard">
            <div className="operational-header">
              <h2>Centro de Operações - Monitoramento Integrado</h2>
              <div className="operational-controls">
                <div className="update-indicator">
                  <span className="status-led operational"></span>
                  <span>Sistema Online - {lastUpdate.toLocaleTimeString()}</span>
                </div>
                <button className="btn-refresh" onClick={updateRealTimeData}>
                  ↻ Atualizar Dados
                </button>
              </div>
            </div>

            {/* Painel de Status Crítico */}
            <div className="critical-status-panel">
              <h3>Painel de Status Operacional</h3>
              <div className="status-overview-grid">
                <div className="status-metric">
                  <div className="metric-header">
                    <span className="status-dot operational"></span>
                    <h4>Operacionais</h4>
                  </div>
                  <div className="metric-value">
                    {Object.values(realTimeData).flatMap(complex => 
                      Object.values(complex).flatMap(park => 
                        Object.values(park).filter(turbine => turbine.status === 'operational')
                      )
                    ).length} / {systemTotals.totalTurbines}
                  </div>
                  <div className="metric-subtitle">Aerogeradores</div>
                </div>
                
                <div className="status-metric warning">
                  <div className="metric-header">
                    <span className="status-dot warning"></span>
                    <h4>Atenção</h4>
                  </div>
                  <div className="metric-value">
                    {Object.values(realTimeData).flatMap(complex => 
                      Object.values(complex).flatMap(park => 
                        Object.values(park).filter(turbine => turbine.status === 'warning')
                      )
                    ).length}
                  </div>
                  <div className="metric-subtitle">Unidades</div>
                </div>
                
                <div className="status-metric maintenance">
                  <div className="metric-header">
                    <span className="status-dot maintenance"></span>
                    <h4>Manutenção</h4>
                  </div>
                  <div className="metric-value">
                    {Object.values(realTimeData).flatMap(complex => 
                      Object.values(complex).flatMap(park => 
                        Object.values(park).filter(turbine => turbine.status === 'maintenance')
                      )
                    ).length}
                  </div>
                  <div className="metric-subtitle">Programadas</div>
                </div>
                
                <div className="status-metric capacity">
                  <div className="metric-header">
                    <span className="capacity-indicator"></span>
                    <h4>Capacidade</h4>
                  </div>
                  <div className="metric-value">
                    {((systemTotals.totalPower / 520) * 100).toFixed(1)}%
                  </div>
                  <div className="metric-subtitle">Utilizada</div>
                </div>
              </div>
            </div>

            {/* Mapa Visual dos Aerogeradores */}
            <div className="turbines-visual-map">
              <h3>Centro de Monitoramento Operacional - Prime Wind</h3>
              <div className="complexes-operational-grid">
                
                {/* Complexo Prime Wind */}
                <div className="complex-operational-section">
                  <div className="complex-operational-header">
                    <h4>Complexo Eólico Prime Wind</h4>
                    <div className="complex-stats">
                      <span className="complex-power">
                        {Object.values(realTimeData).flatMap(complex => 
                          Object.values(complex).flatMap(park => 
                            Object.values(park)
                          )).reduce((sum, turbine) => sum + turbine.power, 0).toFixed(1)} MW
                      </span>
                      <span className="complex-efficiency">
                        {Object.values(realTimeData).flatMap(complex => 
                          Object.values(complex).flatMap(park => 
                            Object.values(park).filter(turbine => turbine.status === 'operational')
                          )
                        ).length}/{Object.values(realTimeData).flatMap(complex => 
                          Object.values(complex).flatMap(park => 
                            Object.values(park)
                          )
                        ).length} Online
                      </span>
                      <span className="complex-location">{opcuaTotalTurbines} Turbinas • OPC UA</span>
                    </div>
                  </div>
                  
                  <div className="parks-operational-container">
                    {/* Mostrar todos os 13 parques de todos os complexos */}
                    {Object.keys(realTimeData).map(complexId => (
                      realTimeData[complexId] && Object.keys(realTimeData[complexId]).map((parkId) => {
                        const parkData = realTimeData[complexId][parkId];
                        const parkNumber = parkId.split('-')[2];
                        
                        return (
                          <div key={parkId} className="park-operational-section">
                            <h5>Parque PW-{parkNumber} ({parkId})</h5>
                            <div className="turbines-status-visual">
                              {Object.values(parkData).map(turbine => (
                                <div 
                                  key={turbine.id} 
                                  className={`turbine-visual-indicator ${turbine.status}`}
                                  title={`${turbine.id}\nPotência: ${turbine.power.toFixed(1)} MW\nVento: ${turbine.windSpeed.toFixed(1)} m/s\nStatus: ${
                                    turbine.status === 'operational' ? 'Operacional' : 
                                    turbine.status === 'maintenance' ? 'Manutenção' :
                                    turbine.status === 'warning' ? 'Atenção' : 'Inativo'
                                  }`}
                                  onClick={() => { 
                                    setSelectedTurbine(turbine.id); 
                                    setActiveTab('turbine-details'); 
                                  }}
                                >
                                  <div className="turbine-visual-id">{(turbine.id.split('-').pop() || '').padStart(3, '0')}</div>
                                  <div className="turbine-visual-power">{turbine.power.toFixed(1)}</div>
                                  <svg className="turbine-icon" viewBox="0 0 24 24">
                                    <line x1="12" y1="8" x2="12" y2="20" className={`turbine-tower ${turbine.status}`} strokeWidth="2"/>
                                    <g className={`turbine-rotor ${turbine.status}`}>
                                      <line x1="12" y1="8" x2="12" y2="3" className={`turbine-blades ${turbine.status}`} strokeWidth="1.5"/>
                                      <line x1="12" y1="8" x2="7.7" y2="12.3" className={`turbine-blades ${turbine.status}`} strokeWidth="1.5"/>
                                      <line x1="12" y1="8" x2="16.3" y2="12.3" className={`turbine-blades ${turbine.status}`} strokeWidth="1.5"/>
                                      <circle cx="12" cy="8" r="0.8" className={`turbine-blades ${turbine.status}`}/>
                                    </g>
                                  </svg>
                                </div>
                              ))}
                            </div>
                          </div>
                        );
                      })
                    )).flat()}
                  </div>
                </div>
              </div>
            </div>

            {/* Performance Consolidado */}
            <div className="consolidated-performance">
              <h3>Performance Consolidado do Sistema</h3>
              <div className="performance-metrics-grid">
                <div className="performance-card">
                  <h4>Geração Total</h4>
                  <div className="performance-value">{systemTotals.totalPower.toFixed(1)} MW</div>
                  <div className="performance-trend">
                    <span className="trend-indicator positive">+2.3%</span>
                    <span>vs. hora anterior</span>
                  </div>
                </div>
                
                <div className="performance-card">
                  <h4>Disponibilidade</h4>
                  <div className="performance-value">
                    {(((systemTotals.totalTurbines - Object.values(realTimeData).flatMap(complex => 
                      Object.values(complex).flatMap(park => 
                        Object.values(park).filter(turbine => turbine.status === 'maintenance' || turbine.status === 'inactive')
                      )
                    ).length) / systemTotals.totalTurbines) * 100).toFixed(1)}%
                  </div>
                  <div className="performance-trend">
                    <span className="performance-subtitle">Operacional</span>
                  </div>
                </div>
                
                <div className="performance-card">
                  <h4>Vento Médio</h4>
                  <div className="performance-value">
                    {(Object.values(realTimeData).flatMap(complex => 
                      Object.values(complex).flatMap(park => 
                        Object.values(park)
                      )
                    ).reduce((sum, turbine, _, arr) => sum + turbine.windSpeed / arr.length, 0)).toFixed(1)} m/s
                  </div>
                  <div className="performance-trend">
                    <span className="performance-subtitle">Condições ideais</span>
                  </div>
                </div>
                
                <div className="performance-card">
                  <h4>Eficiência</h4>
                  <div className="performance-value">94.2%</div>
                  <div className="performance-trend">
                    <span className="trend-indicator positive">+0.8%</span>
                    <span>Sistema otimizado</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Legenda Operacional */}
            <div className="operational-legend">
              <h4>Legenda de Status Operacional</h4>
              <div className="legend-grid">
                <div className="legend-item">
                  <div className="turbine-visual-indicator operational small">
                    <div className="turbine-visual-status operational"></div>
                  </div>
                  <span>Operacional - Funcionamento normal</span>
                </div>
                <div className="legend-item">
                  <div className="turbine-visual-indicator warning small">
                    <div className="turbine-visual-status warning"></div>
                  </div>
                  <span>Atenção - Monitoramento requerido</span>
                </div>
                <div className="legend-item">
                  <div className="turbine-visual-indicator maintenance small">
                    <div className="turbine-visual-status maintenance"></div>
                  </div>
                  <span>Manutenção - Programada ou corretiva</span>
                </div>
                <div className="legend-item">
                  <div className="turbine-visual-indicator fault small">
                    <div className="turbine-visual-status fault"></div>
                  </div>
                  <span>Falha - Intervenção necessária</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Tela de Detalhes do Aerogerador */}
        {activeTab === 'turbine-details' && selectedTurbine && (() => {
          const turbineData = getTurbineData(selectedTurbine);
          if (!turbineData) {
            console.warn('Dados da turbina não encontrados:', selectedTurbine);
            return (
              <div className="error-message">
                <h3>Erro ao carregar dados da turbina</h3>
                <p>Turbina: {selectedTurbine}</p>
                <button className="btn-back" onClick={() => setActiveTab('monitoramento')}>
                  ← Voltar ao Monitoramento
                </button>
              </div>
            );
          }

          const getWindDirection = (degrees: number) => {
            const directions = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'];
            return directions[Math.round(degrees / 45) % 8];
          };

          const getParameterClass = (value: number, good: [number, number], warning: [number, number]) => {
            if (value >= good[0] && value <= good[1]) return 'good';
            if (value >= warning[0] && value <= warning[1]) return 'warning';
            return 'critical';
          };

          const maxPower = turbineData.model.includes('147/4.2') ? 4.2 : turbineData.model.includes('172/7.5') ? 7.5 : 2.1;
          const powerPercentage = (turbineData.power / maxPower) * 100;
          
          // Gerar curva de potência e histórico (sem useMemo dentro de callback)
          let powerCurve: {windSpeed: number; power: number; efficiency: number; isMeasured: boolean; isStall: boolean; isReference: boolean; isNominal: boolean}[] = [];
          let dailyHistory: {date: string; fullDate: string; generation: number; availability: string; windAverage: string}[] = [];
          
          try {
            powerCurve = generatePowerCurve(turbineData.model);
          } catch (error) {
            console.error('Erro ao gerar curva de potência:', error);
          }
          
          try {
            dailyHistory = generateDailyHistory(selectedTurbine);
          } catch (error) {
            console.error('Erro ao gerar histórico:', error);
          }

          return (
            <div className="turbine-details-panel">
              <div className="details-header">
                <div className="back-navigation">
                  <button className="btn-back" onClick={() => setActiveTab('monitoramento')}>
                    ← Voltar ao Monitoramento
                  </button>
                </div>
                <h2>Detalhes Operacionais - {selectedTurbine}</h2>
                <div className="turbine-summary">
                  <span className="turbine-model">{turbineData.model}</span>
                  <span className={`status-badge ${turbineData.status}`}>
                    {turbineData.status === 'operational' ? 'Operacional' : 
                     turbineData.status === 'maintenance' ? 'Manutenção' :
                     turbineData.status === 'warning' ? 'Atenção' : 'Inativo'}
                  </span>
                </div>
              </div>

              {/* Métricas Principais */}
              <div className="operational-metrics">
                <div className="metrics-grid">
                  <div className="metric-card primary">
                    <h3>Potência Atual</h3>
                    <div className="metric-display large">
                      <span className="metric-value">{turbineData.power.toFixed(1)}</span>
                      <span className="metric-unit">MW</span>
                    </div>
                    <div className="metric-percentage">{powerPercentage.toFixed(1)}% da capacidade</div>
                  </div>

                  <div className="metric-card">
                    <h3>Velocidade do Vento</h3>
                    <div className="metric-display">
                      <span className="metric-value">{turbineData.windSpeed.toFixed(1)}</span>
                      <span className="metric-unit">m/s</span>
                    </div>
                    <div className={`metric-trend ${turbineData.windSpeed > 12 ? 'positive' : turbineData.windSpeed > 8 ? 'stable' : 'negative'}`}>
                      {turbineData.windSpeed > 12 ? '↑' : turbineData.windSpeed > 8 ? '→' : '↓'} {turbineData.windSpeed > 8 ? 'Ideal' : 'Baixo'}
                    </div>
                  </div>

                  <div className="metric-card">
                    <h3>RPM do Rotor</h3>
                    <div className="metric-display">
                      <span className="metric-value">{turbineData.rpm.toFixed(1)}</span>
                      <span className="metric-unit">RPM</span>
                    </div>
                    <div className="metric-trend stable">→ Estável</div>
                  </div>

                  <div className="metric-card">
                    <h3>Direção do Vento</h3>
                    <div className="metric-display">
                      <span className="metric-value">{turbineData.windDirection}°</span>
                      <span className="metric-unit">{getWindDirection(turbineData.windDirection)}</span>
                    </div>
                    <div className="metric-trend">Yaw: {turbineData.yawAngle}°</div>
                  </div>
                </div>
              </div>

              {/* Parâmetros Técnicos */}
              <div className="technical-parameters">
                <h3>Parâmetros Técnicos</h3>
                <div className="parameters-grid">
                  <div className="parameter-section">
                    <h4>Sistema Elétrico</h4>
                    <div className="parameter-list">
                      <div className="parameter-item">
                        <span className="param-label">Tensão do Gerador:</span>
                        <span className={`param-value ${getParameterClass(turbineData.voltage, [680, 700], [670, 710])}`}>
                          {turbineData.voltage.toFixed(0)}V
                        </span>
                      </div>
                      <div className="parameter-item">
                        <span className="param-label">Corrente:</span>
                        <span className={`param-value ${turbineData.current > 0 ? 'good' : 'critical'}`}>
                          {turbineData.current.toFixed(0)}A
                        </span>
                      </div>
                      <div className="parameter-item">
                        <span className="param-label">Frequência:</span>
                        <span className={`param-value ${getParameterClass(turbineData.frequency, [49.9, 50.1], [49.7, 50.3])}`}>
                          {turbineData.frequency.toFixed(1)}Hz
                        </span>
                      </div>
                      <div className="parameter-item">
                        <span className="param-label">Fator de Potência:</span>
                        <span className={`param-value ${getParameterClass(turbineData.powerFactor, [0.95, 1.0], [0.90, 0.95])}`}>
                          {turbineData.powerFactor.toFixed(2)}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="parameter-section">
                    <h4>Temperaturas</h4>
                    <div className="parameter-list">
                      <div className="parameter-item">
                        <span className="param-label">Gerador:</span>
                        <span className={`param-value ${getParameterClass(turbineData.generatorTemp, [50, 70], [70, 80])}`}>
                          {turbineData.generatorTemp.toFixed(0)}°C
                        </span>
                      </div>
                      <div className="parameter-item">
                        <span className="param-label">Caixa de Engrenagem:</span>
                        <span className={`param-value ${getParameterClass(turbineData.gearboxTemp, [60, 75], [75, 85])}`}>
                          {turbineData.gearboxTemp.toFixed(0)}°C
                        </span>
                      </div>
                      <div className="parameter-item">
                        <span className="param-label">Óleo Hidráulico:</span>
                        <span className={`param-value ${getParameterClass(turbineData.oilTemp, [30, 45], [45, 55])}`}>
                          {turbineData.oilTemp.toFixed(0)}°C
                        </span>
                      </div>
                      <div className="parameter-item">
                        <span className="param-label">Rolamentos:</span>
                        <span className={`param-value ${getParameterClass(turbineData.bearingTemp, [40, 60], [60, 70])}`}>
                          {turbineData.bearingTemp.toFixed(0)}°C
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="parameter-section">
                    <h4>Sistema Mecânico</h4>
                    <div className="parameter-list">
                      <div className="parameter-item">
                        <span className="param-label">Vibração Nacele:</span>
                        <span className={`param-value ${getParameterClass(turbineData.vibration, [1.0, 2.5], [2.5, 4.0])}`}>
                          {turbineData.vibration.toFixed(1)} mm/s
                        </span>
                      </div>
                      <div className="parameter-item">
                        <span className="param-label">Pressão Hidráulica:</span>
                        <span className={`param-value ${getParameterClass(turbineData.hydraulicPressure, [160, 190], [150, 200])}`}>
                          {turbineData.hydraulicPressure.toFixed(0)} bar
                        </span>
                      </div>
                      <div className="parameter-item">
                        <span className="param-label">Ângulo das Pás:</span>
                        <span className={`param-value good`}>
                          {turbineData.bladeAngle.toFixed(1)}°
                        </span>
                      </div>
                      <div className="parameter-item">
                        <span className="param-label">Torque:</span>
                        <span className={`param-value good`}>
                          {turbineData.torque.toFixed(0)} Nm
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Curva de Potência */}
              <div className="power-curve-section">
                <h3>Curva de Potência - {turbineData.model}</h3>
                <div className="chart-container">
                  <div className="chart-header">
                    <div className="current-point">
                      <span>Ponto Atual: </span>
                      <strong>{turbineData.windSpeed.toFixed(1)} m/s → {turbineData.power.toFixed(1)} MW ({powerPercentage.toFixed(1)}%)</strong>
                    </div>
                    <div className="curve-specs">
                      <div className="spec-item">
                        <span className="spec-label">Potência Nominal:</span>
                        <span className="spec-value">{maxPower} MW</span>
                      </div>
                      <div className="spec-item">
                        <span className="spec-label">Vel. Cut-in:</span>
                        <span className="spec-value">3.0 m/s</span>
                      </div>
                      <div className="spec-item">
                        <span className="spec-label">Vel. Nominal:</span>
                        <span className="spec-value">11.0 m/s</span>
                      </div>
                      <div className="spec-item">
                        <span className="spec-label">Vel. Cut-out:</span>
                        <span className="spec-value">20.0 m/s</span>
                      </div>
                      <div className="spec-item">
                        <span className="spec-label">Cp (Coef. Potência):</span>
                        <span className="spec-value" style={{color: '#007bff'}}>0.50</span>
                      </div>
                      <div className="spec-item">
                        <span className="spec-label">Eficiência Atual:</span>
                        <span className="spec-value" style={{color: turbineData.windSpeed >= 17 ? '#dc3545' : '#28a745'}}>
                          {(() => {
                            const currentPoint = powerCurve?.find(p => Math.abs(p.windSpeed - turbineData.windSpeed) < 0.1);
                            return currentPoint ? `${(currentPoint.efficiency * 100).toFixed(1)}%` : '99.0%';
                          })()}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="power-curve-chart">
                    <svg viewBox="0 0 800 400" className="curve-svg">
                      {/* Grid lines */}
                      <defs>
                        <pattern id="grid" width="40" height="40" patternUnits="userSpaceOnUse">
                          <path d="M 40 0 L 0 0 0 40" fill="none" stroke="rgba(255,255,255,0.1)" strokeWidth="1"/>
                        </pattern>
                      </defs>
                      <rect width="100%" height="100%" fill="url(#grid)" />
                      
                      {/* Axes */}
                      <line x1="80" y1="350" x2="750" y2="350" stroke="#FFD700" strokeWidth="2"/>
                      <line x1="80" y1="50" x2="80" y2="350" stroke="#FFD700" strokeWidth="2"/>
                      
                      {/* X-axis labels (Wind Speed) */}
                      {[0, 5, 10, 15, 20].map(speed => (
                        <g key={speed}>
                          <line x1={80 + (speed * 26)} y1="345" x2={80 + (speed * 26)} y2="355" stroke="#FFD700"/>
                          <text x={80 + (speed * 26)} y="375" textAnchor="middle" fill="#adb5bd" fontSize="12">
                            {speed}
                          </text>
                        </g>
                      ))}
                      
                      {/* Y-axis labels (Power) */}
                      {Array.from({length: 6}, (_, i) => (maxPower / 5) * i).map(power => (
                        <g key={power}>
                          <line x1="75" y1={350 - (power / maxPower * 300)} x2="85" y2={350 - (power / maxPower * 300)} stroke="#FFD700"/>
                          <text x="70" y={350 - (power / maxPower * 300) + 5} textAnchor="end" fill="#adb5bd" fontSize="12">
                            {power.toFixed(1)}
                          </text>
                        </g>
                      ))}
                      
                      {/* Power curve scatter plot baseada em dados históricos */}
                      {powerCurve && powerCurve.length > 0 && (
                        <>
                          {/* Curva de referência (linha vermelha ideal) */}
                          <path
                            d={powerCurve
                              .filter(point => point.isReference)
                              .map((point, i) => 
                                `${i === 0 ? 'M' : 'L'} ${80 + point.windSpeed * 26} ${350 - (point.power / maxPower * 300)}`
                              ).join(' ')}
                            stroke="#dc3545"
                            strokeWidth="2"
                            fill="none"
                            opacity="0.8"
                          />
                          
                          {/* Pontos dispersos (scatter plot) */}
                          {powerCurve.filter(point => !point.isReference).map((point, i) => {
                            const x = 80 + point.windSpeed * 26;
                            const y = 350 - (point.power / maxPower * 300);
                            
                            // Definir cor baseada na velocidade do vento (como na imagem)
                            let color = '#1f77b4'; // Azul para velocidades baixas/médias
                            if (point.windSpeed >= 12) {
                              color = '#ff7f0e'; // Laranja para zona nominal
                            } else if (point.isStall) {
                              color = '#2ca02c'; // Verde para pontos na zona de stall
                            }
                            
                            return (
                              <circle
                                key={`scatter-${i}`}
                                cx={x}
                                cy={y}
                                r="2"
                                fill={color}
                                opacity="0.7"
                              />
                            );
                          })}
                        </>
                      )}
                      
                      {/* Legenda scatter plot */}
                      <g>
                        <text x="90" y="25" fill="#adb5bd" fontSize="12" fontFamily="Exo 2" fontWeight="bold">
                          Curva de Potência - Dados Históricos de Campo
                        </text>
                        <text x="90" y="40" fill="#dc3545" fontSize="10" fontFamily="Exo 2">
                          — Curva de Referência
                        </text>
                        <circle cx="250" cy="35" r="3" fill="#1f77b4" opacity="0.7" />
                        <text x="260" y="39" fill="#1f77b4" fontSize="10" fontFamily="Exo 2">
                          Medições 3-11 m/s
                        </text>
                        <circle cx="370" cy="35" r="3" fill="#2ca02c" opacity="0.7" />
                        <text x="380" y="39" fill="#2ca02c" fontSize="10" fontFamily="Exo 2">
                          Stall Detectado (10-12 m/s)
                        </text>
                        <circle cx="520" cy="35" r="3" fill="#ff7f0e" opacity="0.7" />
                        <text x="530" y="39" fill="#ff7f0e" fontSize="10" fontFamily="Exo 2">
                          Zona Nominal (11-20 m/s)
                        </text>
                      </g>
                      
                      {/* Current operating point */}
                      <circle
                        cx={Math.min(730, 80 + turbineData.windSpeed * 26)}
                        cy={Math.max(60, 350 - (turbineData.power / maxPower * 300))}
                        r="8"
                        fill="#FFD700"
                        stroke="#fff"
                        strokeWidth="2"
                      />
                      
                      {/* Linhas de referência do ponto atual */}
                      <line 
                        x1={Math.min(730, 80 + turbineData.windSpeed * 26)} 
                        y1="350" 
                        x2={Math.min(730, 80 + turbineData.windSpeed * 26)} 
                        y2={Math.max(60, 350 - (turbineData.power / maxPower * 300))}
                        stroke="#FFD700" 
                        strokeWidth="1" 
                        strokeDasharray="5,5" 
                        opacity="0.7"
                      />
                      <line 
                        x1="80" 
                        y1={Math.max(60, 350 - (turbineData.power / maxPower * 300))} 
                        x2={Math.min(730, 80 + turbineData.windSpeed * 26)} 
                        y2={Math.max(60, 350 - (turbineData.power / maxPower * 300))}
                        stroke="#FFD700" 
                        strokeWidth="1" 
                        strokeDasharray="5,5" 
                        opacity="0.7"
                      />
                      
                      
                      {/* Axis titles */}
                      <text x="415" y="395" textAnchor="middle" fill="#FFD700" fontSize="14" fontWeight="bold">
                        Velocidade do Vento (m/s)
                      </text>
                      <text x="25" y="200" textAnchor="middle" fill="#FFD700" fontSize="14" fontWeight="bold" 
                            transform="rotate(-90 25 200)">
                        Potência (MW)
                      </text>
                    </svg>
                  </div>
                </div>
              </div>

              {/* Histórico de Geração Diária */}
              <div className="daily-history-section">
                <h3>Histórico de Geração - Últimos 7 Dias</h3>
                <div className="history-container">
                  <div className="history-summary">
                    <div className="summary-item">
                      <span className="summary-label">Geração Total (7 dias):</span>
                      <span className="summary-value">
                        {dailyHistory && dailyHistory.length > 0 ? 
                          dailyHistory.reduce((sum, day) => sum + day.generation, 0).toFixed(1) : '0.0'} MWh
                      </span>
                    </div>
                    <div className="summary-item">
                      <span className="summary-label">Média Diária:</span>
                      <span className="summary-value">
                        {dailyHistory && dailyHistory.length > 0 ? 
                          (dailyHistory.reduce((sum, day) => sum + day.generation, 0) / 7).toFixed(1) : '0.0'} MWh/dia
                      </span>
                    </div>
                    <div className="summary-item">
                      <span className="summary-label">Disponibilidade Média:</span>
                      <span className="summary-value">
                        {dailyHistory && dailyHistory.length > 0 ? 
                          (dailyHistory.reduce((sum, day) => sum + parseFloat(day.availability), 0) / 7).toFixed(0) : '0'}%
                      </span>
                    </div>
                  </div>
                  
                  <div className="history-chart">
                    <svg viewBox="0 0 800 300" className="history-svg">
                      {/* Grid */}
                      <rect width="100%" height="100%" fill="url(#grid)" />
                      
                      {/* Axes */}
                      <line x1="80" y1="250" x2="720" y2="250" stroke="#FFD700" strokeWidth="2"/>
                      <line x1="80" y1="50" x2="80" y2="250" stroke="#FFD700" strokeWidth="2"/>
                      
                      {/* Bars and labels */}
                      {dailyHistory && dailyHistory.length > 0 && dailyHistory.map((day, index) => {
                        const maxGeneration = Math.max(...dailyHistory.map(d => d.generation));
                        const barHeight = maxGeneration > 0 ? (day.generation / maxGeneration) * 180 : 0;
                        const x = 120 + index * 80;
                        
                        return (
                          <g key={index}>
                            {/* Bar */}
                            <rect
                              x={x - 25}
                              y={250 - barHeight}
                              width="50"
                              height={barHeight}
                              fill={day.availability === '10' ? '#dc3545' : '#28a745'}
                              rx="4"
                            />
                            
                            {/* Value on top */}
                            <text x={x} y={250 - barHeight - 10} textAnchor="middle" fill="#FFD700" fontSize="12" fontWeight="bold">
                              {day.generation}
                            </text>
                            
                            {/* Date label */}
                            <text x={x} y="270" textAnchor="middle" fill="#adb5bd" fontSize="11">
                              {day.date}
                            </text>
                            
                            {/* Availability indicator */}
                            <text x={x} y="285" textAnchor="middle" fill={day.availability === '10' ? '#dc3545' : '#28a745'} fontSize="10">
                              {day.availability}%
                            </text>
                          </g>
                        );
                      })}
                      
                      {/* Y-axis title */}
                      <text x="25" y="150" textAnchor="middle" fill="#FFD700" fontSize="14" fontWeight="bold" 
                            transform="rotate(-90 25 150)">
                        Geração (MWh)
                      </text>
                    </svg>
                  </div>
                  
                  <div className="history-table">
                    <table>
                      <thead>
                        <tr>
                          <th>Data</th>
                          <th>Geração (MWh)</th>
                          <th>Disponibilidade</th>
                          <th>Vento Médio</th>
                        </tr>
                      </thead>
                      <tbody>
                        {dailyHistory && dailyHistory.length > 0 ? dailyHistory.map((day, index) => (
                          <tr key={index} className={day.availability === '10' ? 'maintenance-day' : ''}>
                            <td>{day.fullDate}</td>
                            <td>{day.generation} MWh</td>
                            <td>
                              <span className={`availability-badge ${day.availability === '10' ? 'low' : 'good'}`}>
                                {day.availability}%
                              </span>
                            </td>
                            <td>{day.windAverage} m/s</td>
                          </tr>
                        )) : (
                          <tr>
                            <td colSpan={4}>Nenhum dado histórico disponível</td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>

              {/* Controles e Comandos */}

              {/* Controles e Comandos */}
              <div className="control-panel">
                <h3>Controles de Operação</h3>
                <div className="controls-grid">
                  <div className="control-section">
                    <h4>Comandos Principais</h4>
                    <div className="control-buttons">
                      <button className="control-btn start" disabled={turbineData.status === 'operational'}>
                        Iniciar
                      </button>
                      <button className="control-btn stop" disabled={turbineData.status === 'inactive'}>
                        Parar
                      </button>
                      <button className="control-btn maintenance" disabled={turbineData.status === 'maintenance'}>
                        Manutenção
                      </button>
                      <button className="control-btn reset">Reset</button>
                    </div>
                  </div>

                  <div className="control-section">
                    <h4>Configurações</h4>
                    <div className="control-settings">
                      <div className="setting-item">
                        <label>Potência Limite:</label>
                        <input type="range" min="0" max="100" defaultValue={Math.round(powerPercentage)} />
                        <span>{Math.round(powerPercentage)}%</span>
                      </div>
                      <div className="setting-item">
                        <label>Modo Operação:</label>
                        <select defaultValue="automatic">
                          <option value="automatic">Automático</option>
                          <option value="manual">Manual</option>
                          <option value="test">Teste</option>
                        </select>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Histórico e Eventos */}
              <div className="events-log">
                <h3>Eventos Recentes</h3>
                <div className="events-list">
                  <div className="event-item info">
                    <span className="event-time">{lastUpdate.toLocaleTimeString()}</span>
                    <span className="event-message">Dados atualizados em tempo real</span>
                    <span className="event-type">Sistema</span>
                  </div>
                  {turbineData.status === 'warning' && (
                    <div className="event-item warning">
                      <span className="event-time">{lastUpdate.toLocaleTimeString()}</span>
                      <span className="event-message">Status de atenção detectado</span>
                      <span className="event-type">Alerta</span>
                    </div>
                  )}
                  {turbineData.gearboxTemp > 80 && (
                    <div className="event-item warning">
                      <span className="event-time">{lastUpdate.toLocaleTimeString()}</span>
                      <span className="event-message">Temperatura da caixa de engrenagem elevada</span>
                      <span className="event-type">Alerta</span>
                    </div>
                  )}
                  <div className="event-item info">
                    <span className="event-time">{new Date(lastUpdate.getTime() - 300000).toLocaleTimeString()}</span>
                    <span className="event-message">Ajuste automático do ângulo das pás</span>
                    <span className="event-type">Controle</span>
                  </div>
                  {turbineData.power > (maxPower * 0.95) && (
                    <div className="event-item info">
                      <span className="event-time">{new Date(lastUpdate.getTime() - 600000).toLocaleTimeString()}</span>
                      <span className="event-message">Produção próxima ao máximo</span>
                      <span className="event-type">Operação</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })()}

          {activeTab === 'turbinas' && (
            <div className="turbines-panel">
              <h2>Monitoramento de Turbinas</h2>
              {/* Conteúdo original da aba turbinas permanece aqui */}
            </div>
          )}

          {activeTab === 'opcua-simulator' && (
            <div className="opcua-simulator-panel">
              <h2>OPC UA Simulator - Emulador de Aerogerador</h2>
              
              <div className="opcua-emulator">
                <div className="emulator-header">
                  <h3>Servidor OPC UA - Turbina Virtual</h3>
                  <div className="server-status">
                    <span className={`status-indicator ${opcuaServerStatus === 'running' ? 'running' : opcuaServerStatus === 'stopped' ? 'stopped' : 'transitioning'}`}></span>
                    <span>
                      {opcuaServerStatus === 'running' && 'Servidor Ativo'}
                      {opcuaServerStatus === 'stopped' && 'Servidor Parado'}
                      {opcuaServerStatus === 'starting' && 'Iniciando...'}
                      {opcuaServerStatus === 'stopping' && 'Parando...'}
                    </span>
                  </div>
                </div>

                <div className="emulator-content">
                  <div className="opcua-servers-grid">
                    {/* Servidor OPC UA Padrão - PW01-T-001 */}
                    <div className="server-info-card">
                      <h4>🔧 Servidor OPC UA Padrão - PW01-T-001</h4>
                      <div className="connection-grid">
                        <div className="connection-item">
                          <label>Turbina:</label>
                          <span className="connection-value">PW01-T-001 (WEG AGW110/2.1)</span>
                        </div>
                        <div className="connection-item">
                          <label>Endereço:</label>
                          <span className="connection-value">opc.tcp://localhost:4334/PrimeWindTurbine</span>
                        </div>
                        <div className="connection-item">
                          <label>Porta:</label>
                          <span className="connection-value">4334</span>
                        </div>
                        <div className="connection-item">
                          <label>Autenticação:</label>
                          <span className="connection-value">Nenhuma (Padrão)</span>
                        </div>
                        <div className="connection-item">
                          <label>Status:</label>
                          <span className={`connection-status ${opcuaServerStatus === 'running' ? 'active' : 'inactive'}`}>
                            {opcuaServerStatus === 'running' ? '🟢 Ativo' : '🔴 Inativo'}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Servidor OPC UA Seguro - PW02-T-002 */}
                    <div className="server-info-card">
                      <h4>🔐 Servidor OPC UA Seguro - PW02-T-002</h4>
                      <div className="connection-grid">
                        <div className="connection-item">
                          <label>Turbina:</label>
                          <span className="connection-value">PW02-T-002 (WEG AGW147/4.2)</span>
                        </div>
                        <div className="connection-item">
                          <label>Endereço:</label>
                          <span className="connection-value">opc.tcp://localhost:4336/PrimeWindTurbineSecure</span>
                        </div>
                        <div className="connection-item">
                          <label>Porta:</label>
                          <span className="connection-value">4336</span>
                        </div>
                        <div className="connection-item">
                          <label>Autenticação:</label>
                          <span className="connection-value">SCADA/SCADA (Usuário/Senha)</span>
                        </div>
                        <div className="connection-item">
                          <label>Status:</label>
                          <span className="connection-status active">🟢 Ativo</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="variables-section">
                    <h4>Variáveis OPC UA Disponíveis</h4>
                    <div className="variables-table">
                      <div className="table-header">
                        <span>Nome da Variável</span>
                        <span>Node ID</span>
                        <span>Tipo</span>
                        <span>Valor Atual</span>
                        <span>Unidade</span>
                      </div>
                      
                      {(() => {
                        const sampleTurbine = getTurbineData('PW01-T-001') || {
                          power: 1.85,
                          windSpeed: 12.5,
                          rpm: 18.2,
                          generatorTemp: 65.2,
                          gearboxTemp: 58.7,
                          vibration: 1.23,
                          status: 'operational',
                          bladeAngle: 12.5
                        };
                        return (
                          <>
                            <div className="table-row">
                              <span>Potência Ativa</span>
                              <span>ns=2;s=TurbineData.ActivePower</span>
                              <span>Double</span>
                              <span>{sampleTurbine.power.toFixed(2)}</span>
                              <span>MW</span>
                            </div>
                            
                            <div className="table-row">
                              <span>Velocidade do Vento</span>
                              <span>ns=2;s=TurbineData.WindSpeed</span>
                              <span>Double</span>
                              <span>{sampleTurbine.windSpeed.toFixed(1)}</span>
                              <span>m/s</span>
                            </div>
                            
                            <div className="table-row">
                              <span>RPM do Rotor</span>
                              <span>ns=2;s=TurbineData.RotorRPM</span>
                              <span>Double</span>
                              <span>{sampleTurbine.rpm.toFixed(1)}</span>
                              <span>rpm</span>
                            </div>
                            
                            <div className="table-row">
                              <span>Temperatura do Gerador</span>
                              <span>ns=2;s=TurbineData.GeneratorTemp</span>
                              <span>Double</span>
                              <span>{sampleTurbine.generatorTemp.toFixed(1)}</span>
                              <span>°C</span>
                            </div>
                            
                            <div className="table-row">
                              <span>Temperatura da Caixa</span>
                              <span>ns=2;s=TurbineData.GearboxTemp</span>
                              <span>Double</span>
                              <span>{sampleTurbine.gearboxTemp.toFixed(1)}</span>
                              <span>°C</span>
                            </div>
                            
                            <div className="table-row">
                              <span>Vibração</span>
                              <span>ns=2;s=TurbineData.Vibration</span>
                              <span>Double</span>
                              <span>{sampleTurbine.vibration.toFixed(2)}</span>
                              <span>mm/s</span>
                            </div>
                            
                            <div className="table-row">
                              <span>Status Operacional</span>
                              <span>ns=2;s=TurbineData.OperationalStatus</span>
                              <span>String</span>
                              <span>{sampleTurbine.status.toUpperCase()}</span>
                              <span>-</span>
                            </div>
                            
                            <div className="table-row">
                              <span>Ângulo das Pás</span>
                              <span>ns=2;s=TurbineData.BladeAngle</span>
                              <span>Double</span>
                              <span>{sampleTurbine.bladeAngle.toFixed(1)}</span>
                              <span>°</span>
                            </div>
                            
                            <div className="table-row">
                              <span>Energia Acumulada</span>
                              <span>ns=2;s=TurbineData.EnergyAccumulated</span>
                              <span>Double</span>
                              <span>{(sampleTurbine.power * 24 * 30).toFixed(0)}</span>
                              <span>MWh</span>
                            </div>
                            
                            <div className="table-row">
                              <span>Disponibilidade</span>
                              <span>ns=2;s=TurbineData.Availability</span>
                              <span>Double</span>
                              <span>{(98.5).toFixed(1)}</span>
                              <span>%</span>
                            </div>
                          </>
                        );
                      })()}
                    </div>
                  </div>

                  <div className="emulator-controls">
                    <h4>Controles do Emulador</h4>
                    <div className="controls-grid">
                      <button 
                        className={opcuaServerStatus === 'running' ? "btn-secondary" : "btn-primary"}
                        onClick={opcuaServerStatus === 'running' ? stopOpcuaServer : startOpcuaServer}
                        disabled={opcuaServerStatus === 'starting' || opcuaServerStatus === 'stopping'}
                      >
                        {opcuaServerStatus === 'running' && '⏹️ Parar Servidor'}
                        {opcuaServerStatus === 'stopped' && '▶️ Iniciar Servidor OPC UA'}
                        {opcuaServerStatus === 'starting' && '⏳ Iniciando...'}
                        {opcuaServerStatus === 'stopping' && '⏳ Parando...'}
                      </button>
                      <button 
                        className="btn-secondary"
                        onClick={testOpcuaConnection}
                        disabled={opcuaServerStatus !== 'running'}
                      >
                        🔗 Teste de Conexão
                      </button>
                      <button 
                        className="btn-secondary"
                        onClick={exportOpcuaConfig}
                      >
                        📄 Exportar Configuração
                      </button>
                      <button 
                        className={opcuaConnectionTested ? "btn-success" : "btn-secondary"}
                        onClick={() => window.open('https://www.prosysopc.com/products/opc-ua-client/', '_blank')}
                      >
                        🔧 Cliente OPC UA Externo
                      </button>
                    </div>
                    
                    <div className="emulator-info">
                      <p><strong>Simulação Ativa:</strong> O emulador está gerando dados em tempo real para integração com clientes OPC UA.</p>
                      <p><strong>Frequência de Atualização:</strong> 5 segundos (configurável)</p>
                      <p><strong>Protocolo:</strong> OPC UA 1.04</p>
                      <p><strong>Segurança:</strong> None (para desenvolvimento)</p>
                      {opcuaConfig.exported && (
                        <p><strong>Última Exportação:</strong> {opcuaConfig.lastExport?.toLocaleString()}</p>
                      )}
                      {opcuaConnectionTested && (
                        <p><strong>Teste de Conexão:</strong> ✅ Último teste realizado com sucesso</p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'turbine-simulator' && (
            <div className="turbine-simulator-panel">
              <h3>Simulador de Turbinas</h3>
              <p>Sistema de controle manual disponível em breve.</p>
              <div className="turbine-status-grid">
                {Object.values(realTimeData).flatMap(complex => 
                  Object.values(complex).flatMap(park => 
                    Object.values(park)
                  )
                ).map(turbine => (
                  <div key={turbine.id} className="turbine-status-card">
                    <h4>{turbine.id}</h4>
                    <div className="turbine-metrics">
                      <div><strong>Potência:</strong> {turbine.power.toFixed(1)} MW</div>
                      <div><strong>Vento:</strong> {turbine.windSpeed.toFixed(1)} m/s</div>
                      <div><strong>RPM:</strong> {turbine.rpm.toFixed(0)}</div>
                      <div><strong>Temp:</strong> {turbine.generatorTemp.toFixed(0)}°C</div>
                      <div><strong>Status:</strong> {turbine.status}</div>
                    </div>
                    <div className="manual-control-placeholder">
                      <button className="btn-secondary" disabled>
                        🎮 Controle Manual (Em breve)
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Simulador antigo temporariamente desabilitado para correção de erros */}
          {false && (
            <div className="turbine-simulator-panel">
              <h2>Turbine Simulator - Controle Manual</h2>
              
              <div className="simulator-disabled">
                <p>Seção temporariamente desabilitada para correção de erros de compilação.</p>
              </div>
            </div>
          )}

          {/* Controles manuais temporariamente desabilitados para correção */}

          {/* Interface de Configuração de Aerogerador OPC UA */}
          {activeTab === 'turbine-config' && (
            <div className="turbine-config-panel">
              <h2>Configurar Novo Aerogerador OPC UA</h2>
              <p>Funcionalidade temporariamente simplificada.</p>
            </div>
          )}

          {/* Sistema de Gerenciamento de Alarmes */}
          {activeTab === 'alarmes' && (
            <div className="alarms-management-panel">
              <div className="alarms-header">
                <h2>Sistema de Gerenciamento de Alarmes</h2>
                <div className="alarms-summary">
                  <div className="alarm-counter active">
                    <span className="counter-value">{failureNotifications.length + operationalAlarms.length}</span>
                    <span className="counter-label">Ativos</span>
                  </div>
                  <div className="alarm-counter total">
                    <span className="counter-value">{failureNotifications.length + operationalAlarms.length}</span>
                    <span className="counter-label">Total</span>
                  </div>
                </div>
              </div>

              {/* Alarmes Ativos */}
              <div className="active-alarms-section">
                <h3>Alarmes Ativos</h3>
                {(failureNotifications.length > 0 || operationalAlarms.length > 0) ? (
                  <div className="alarms-table">
                    <div className="table-header">
                      <div className="col-time">Horário</div>
                      <div className="col-turbine">Turbina</div>
                      <div className="col-description">Descrição</div>
                      <div className="col-type">Tipo</div>
                      <div className="col-status">Status</div>
                      <div className="col-actions">Ações</div>
                    </div>
                    <div className="table-body">
                      {/* Mostrar alarmes operacionais simulados primeiro */}
                      {operationalAlarms.map((alarm) => (
                        <div key={alarm.id} className={`alarm-row ${alarm.type}`}>
                          <div className="col-time">
                            {alarm.timestamp.toLocaleString()}
                          </div>
                          <div className="col-turbine">
                            <span className="turbine-id">{alarm.turbineId}</span>
                            <span className="turbine-model">{alarm.turbine.model}</span>
                          </div>
                          <div className="col-description">
                            {alarm.description}
                          </div>
                          <div className="col-type">
                            <span className={`alarm-type-badge warning`}>
                              ⚠️ ATENÇÃO
                            </span>
                          </div>
                          <div className="col-status">
                            <span className="status-badge pending">
                              Pendente
                            </span>
                          </div>
                          <div className="col-actions">
                            <button className="btn-acknowledge">
                              Reconhecer
                            </button>
                          </div>
                        </div>
                      ))}
                      
                      {/* Mostrar notificações de falha existentes */}
                      {failureNotifications.map((notification) => (
                        <div key={notification.id} className={`alarm-row ${notification.type}`}>
                          <div className="col-time">
                            {new Date(notification.timestamp).toLocaleString()}
                          </div>
                          <div className="col-turbine">
                            <span className="turbine-id">{notification.turbine.id}</span>
                            <span className="turbine-model">{notification.turbine.model}</span>
                          </div>
                          <div className="col-description">
                            {notification.turbine.status === 'warning' && 'Parâmetros operacionais fora do normal'}
                            {notification.turbine.status === 'maintenance' && 'Turbina em modo de manutenção'}
                            {notification.turbine.status === 'inactive' && 'Turbina com falha crítica'}
                          </div>
                          <div className="col-type">
                            <span className={`alarm-type-badge ${notification.turbine.status}`}>
                              {notification.turbine.status === 'warning' && '⚠️ ATENÇÃO'}
                              {notification.turbine.status === 'maintenance' && '🔧 MANUTENÇÃO'}
                              {notification.turbine.status === 'inactive' && '🚨 FALHA'}
                            </span>
                          </div>
                          <div className="col-status">
                            <span className={`status-badge ${notification.acknowledged ? 'acknowledged' : 'pending'}`}>
                              {notification.acknowledged ? 'Reconhecido' : 'Pendente'}
                            </span>
                          </div>
                          <div className="col-actions">
                            {!notification.acknowledged && (
                              <button 
                                className="btn-acknowledge"
                                onClick={() => {
                                  // Acknowledging alarm logic
                                  setFailureNotifications(prev => 
                                    prev.map(n => 
                                      n.id === notification.id 
                                        ? { ...n, acknowledged: true }
                                        : n
                                    )
                                  );
                                  setAcknowledgedAlarms(prev => new Set(Array.from(prev).concat(notification.turbine.id)));
                                }}
                              >
                                Reconhecer
                              </button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="no-alarms">
                    <div className="no-alarms-icon">✅</div>
                    <h4>Nenhum Alarme Ativo</h4>
                    <p>Todas as turbinas estão operando normalmente</p>
                  </div>
                )}
              </div>

              {/* Histórico de Alarmes */}
              <div className="historical-alarms-section">
                <div className="section-header">
                  <h3>Extração de Alarmes Históricos</h3>
                  <div className="period-selector">
                    <label>Período:</label>
                    <input 
                      type="date" 
                      defaultValue={new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]}
                      className="date-input"
                    />
                    <span>até</span>
                    <input 
                      type="date" 
                      defaultValue={new Date().toISOString().split('T')[0]}
                      className="date-input"
                    />
                    <button className="btn-extract">
                      📊 Extrair Relatório
                    </button>
                  </div>
                </div>
                
                <div className="historical-summary">
                  <div className="summary-card">
                    <div className="summary-icon">📈</div>
                    <div className="summary-content">
                      <h4>Alarmes nos últimos 7 dias</h4>
                      <p className="summary-value">{alarmStats.totalLast7Days}</p>
                      <p className="summary-label">Total de ocorrências</p>
                    </div>
                  </div>
                  
                  <div className="summary-card">
                    <div className="summary-icon">⚠️</div>
                    <div className="summary-content">
                      <h4>Turbina com mais alarmes</h4>
                      <p className="summary-value">{alarmStats.mostAlarmsTurbine}</p>
                      <p className="summary-label">
                        {alarmStats.mostAlarmsCount > 0 
                          ? `${alarmStats.mostAlarmsCount} ocorrências`
                          : 'Nenhum alarme registrado'
                        }
                      </p>
                    </div>
                  </div>
                  
                  <div className="summary-card">
                    <div className="summary-icon">🔧</div>
                    <div className="summary-content">
                      <h4>Tipo mais frequente</h4>
                      <p className="summary-value">{alarmStats.mostFrequentType}</p>
                      <p className="summary-label">
                        {alarmStats.mostFrequentCount > 0 
                          ? `${alarmStats.mostFrequentCount} ocorrências`
                          : 'Nenhum dado disponível'
                        }
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Sistema de Análise de Dados OPC */}
          {activeTab === 'analise' && (
            <div className="analysis-panel">
              <div className="analysis-header">
                <h2>Análise e Exportação de Dados OPC</h2>
                <p>Exporte histórico das variáveis OPC UA dos aerogeradores</p>
              </div>

              <div className="export-options">
                <div className="export-section">
                  <h3>🔗 Seleção de Turbinas</h3>
                  <div className="selection-grid">
                    <div className="selection-option">
                      <input 
                        type="radio" 
                        id="individual" 
                        name="exportType" 
                        value="individual"
                        defaultChecked 
                      />
                      <label htmlFor="individual">
                        <span className="option-title">Turbina Individual</span>
                        <span className="option-description">Selecione uma turbina específica</span>
                      </label>
                    </div>
                    
                    <div className="selection-option">
                      <input 
                        type="radio" 
                        id="complex" 
                        name="exportType" 
                        value="complex" 
                      />
                      <label htmlFor="complex">
                        <span className="option-title">Complexo Completo</span>
                        <span className="option-description">Todas as {opcuaTotalTurbines} turbinas</span>
                      </label>
                    </div>
                    
                    <div className="selection-option">
                      <input 
                        type="radio" 
                        id="multiple" 
                        name="exportType" 
                        value="multiple" 
                      />
                      <label htmlFor="multiple">
                        <span className="option-title">Seleção Múltipla</span>
                        <span className="option-description">Escolha turbinas específicas</span>
                      </label>
                    </div>
                  </div>
                </div>

                <div className="export-section">
                  <h3>📅 Período de Análise</h3>
                  <div className="period-grid">
                    <div className="date-range">
                      <label>Data Inicial:</label>
                      <input type="datetime-local" defaultValue="2025-09-01T00:00" />
                    </div>
                    <div className="date-range">
                      <label>Data Final:</label>
                      <input type="datetime-local" defaultValue="2025-09-04T23:59" />
                    </div>
                  </div>
                </div>

                <div className="export-section">
                  <h3>⚙️ Variáveis OPC</h3>
                  <div className="variables-grid">
                    <div className="variable-category">
                      <h4>Dados de Vento</h4>
                      <label><input type="checkbox" defaultChecked /> Velocidade do Vento (m/s)</label>
                      <label><input type="checkbox" defaultChecked /> Direção do Vento (°)</label>
                      <label><input type="checkbox" /> Densidade do Ar (kg/m³)</label>
                    </div>
                    <div className="variable-category">
                      <h4>Dados Elétricos</h4>
                      <label><input type="checkbox" defaultChecked /> Potência Ativa (MW)</label>
                      <label><input type="checkbox" /> Potência Reativa (MVAr)</label>
                      <label><input type="checkbox" /> Tensão (V)</label>
                      <label><input type="checkbox" /> Corrente (A)</label>
                      <label><input type="checkbox" /> Frequência (Hz)</label>
                    </div>
                    <div className="variable-category">
                      <h4>Dados Mecânicos</h4>
                      <label><input type="checkbox" /> RPM do Rotor</label>
                      <label><input type="checkbox" /> RPM do Gerador</label>
                      <label><input type="checkbox" /> Ângulo do Pitch (°)</label>
                      <label><input type="checkbox" /> Posição da Nacelle (°)</label>
                    </div>
                    <div className="variable-category">
                      <h4>Temperaturas</h4>
                      <label><input type="checkbox" /> Temp. Gerador (°C)</label>
                      <label><input type="checkbox" /> Temp. Óleo (°C)</label>
                      <label><input type="checkbox" /> Temp. Ambiente (°C)</label>
                    </div>
                    <div className="variable-category">
                      <h4>Status Operacional</h4>
                      <label><input type="checkbox" /> Estado da Turbina</label>
                      <label><input type="checkbox" /> Alarmes Ativos</label>
                      <label><input type="checkbox" /> Eficiência (%)</label>
                    </div>
                  </div>
                </div>

                <div className="export-section">
                  <h3>📊 Formato de Exportação</h3>
                  <div className="format-grid">
                    <label>
                      <input type="radio" name="format" value="csv" defaultChecked />
                      <span>CSV (Excel compatível)</span>
                    </label>
                    <label>
                      <input type="radio" name="format" value="json" />
                      <span>JSON (dados estruturados)</span>
                    </label>
                    <label>
                      <input type="radio" name="format" value="xlsx" />
                      <span>Excel (.xlsx)</span>
                    </label>
                  </div>
                </div>

                <div className="export-actions">
                  <button className="preview-button">
                    📋 Visualizar Dados
                  </button>
                  <button className="export-button">
                    💾 Exportar Histórico
                  </button>
                </div>

                <div className="export-info">
                  <div className="info-card">
                    <h4>ℹ️ Informações</h4>
                    <ul>
                      <li>Dados armazenados com resolução de 5 segundos</li>
                      <li>Histórico disponível dos últimos 30 dias</li>
                      <li>Exportações grandes podem demorar alguns minutos</li>
                      <li>Limite máximo: 1 milhão de registros por exportação</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          )}
        </main>
      </div>

      {/* Notificações de falha temporariamente removidas */}
    </div>
  );
};

export default Dashboard;